import React from "react";
import { SECTION_CATALOG } from "./relationships/SectionCatalog";
import {
  isApplicationEntity,
  normalizeEntityType,
} from "./relationships/SectionConfig";
import {
  resolveSectionItems,
  relationshipEdgeIdForSectionItem,
} from "./relationships/RelationshipResolver";
import { enrichmentIncomingCompoundRelMatches, ENRICHMENT_INCOMING_COMPOUND_SECTIONS } from "../utils/enrichmentRelationshipPersist";
import { getValidationRule } from "./relationships/RelationshipRouting";
import { getHumanLabel } from "./relationships/RelationshipHelpers";
import { buildRelationshipCreatesForSection } from "./relationships/relationshipBatchConstants";
import {
  filterApplicationExplorerPayload,
  filterExplorerInterfaceSectionItems,
} from "../utils/explorerVisibility";
import { parseEntityIdArray as parseIdArray } from "../utils/entityIdArrays";
import EntityRelationshipSection from "./EntityRelationshipSection";
import RelationshipAttributesDialog, {
  relationshipAttributesFromItem,
} from "./RelationshipAttributesDialog";
import { getEntityTypes } from "../utils/entityTypes";
import {
  formatFieldDisplayValue,
  formatFieldLabel,
  formatEntityMapStatusLabel,
  formatRelationshipStatusLabel,
} from "../utils/enrichmentJobCard";
import { getProfileByValue } from "../utils/typeProfile";
import { buildEntityTypeAttributesQueryParams } from "../utils/entityTypeAttributes";
import { EMPTY_UNSAVED_ENTITY_DETAIL_SUMMARY } from "../utils/entityDetailLeaveGuard";
import { useDeviceDetection } from "../hooks/useDeviceDetection";
import { userCanContribute } from "../utils/userRoles";

const isEmptyValue = (val) => {
  if (val === null || val === undefined) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === "object" && Object.keys(val).length === 0) return true;
  return false;
};

const formatLabel = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

/** Compact pencil → same handler as header Edit (parent switches to Edit tab). */
const ReadSectionPencil = ({ label, onClick }) => {
  if (typeof onClick !== "function") return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-400 hover:bg-gray-700/80 transition-colors"
      aria-label={`Edit ${label}`}
      title={`Edit ${label}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="h-4 w-4"
        aria-hidden
      >
        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
      </svg>
    </button>
  );
};

const formatRelatedEntityTypeLabel = (typeRaw) => {
  const normalized = normalizeEntityType(typeRaw);
  if (!normalized) return "";
  const profile = getProfileByValue(normalized);
  return profile?.label || formatLabel(normalized);
};

/** Consistent entity name link + type badge for relationship rows. */
const RelatedEntityNameLink = ({
  related,
  currentEntityId,
  onNavigate,
  sectionIsEditing = false,
  /** Mobile: show status inline after the type label. */
  statusLabel = "",
}) => {
  if (!related?.name) return null;
  const typeRaw = related.type || related.entity_type;
  const typeLabel = formatRelatedEntityTypeLabel(typeRaw);
  const canNavigate =
    typeof onNavigate === "function" &&
    !sectionIsEditing &&
    Boolean(related.id) &&
    String(related.id) !== String(currentEntityId || "");

  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      {canNavigate ? (
        <button
          type="button"
          onClick={() =>
            onNavigate({
              id: related.id,
              name: related.name,
              type: typeRaw,
              entity_type: typeRaw,
            })
          }
          className="font-medium text-blue-400 hover:text-blue-300 hover:underline text-left"
        >
          {related.name}
        </button>
      ) : (
        <span className="font-medium text-gray-100">{related.name}</span>
      )}
      {typeLabel ? (
        <span className="text-xs text-gray-400">({typeLabel})</span>
      ) : null}
      {statusLabel ? (
        <span className="text-xs text-gray-400 md:hidden">· {statusLabel}</span>
      ) : null}
    </div>
  );
};

const normalizeAuditJson = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return {};
};

const normalizeAuditFieldKey = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_")
    .trim();

const isMeaningfulAuditField = (key) => {
  const k = normalizeAuditFieldKey(key);
  const hidden = new Set([
    "id",
    "tenant_id",
    "entity_id",
    "entity_type",
    "entity_type_id",
    "created_at",
    "updated_at",
    "version",
    "batch_id",
    "batch_timestamp",
    "custom_properties",
    "is_sample_data",
    "type",
    // Relationship adjacency arrays are noisy in attribute diffs.
    // Relationship changes are better captured via `relationships_diff`.
    "children",
    "predecessors",
    "successors",
  ]);
  return !hidden.has(k);
};

import GenerateDocumentButton from "./GenerateDocumentButton";
import EntitySelectorModal from "./EntitySelectorModal";
import HierarchyParentPickerModal from "./HierarchyParentPickerModal";
import EntityCustomAttributesReadOnly from "./EntityCustomAttributesReadOnly";
import EntityCustomAttributes from "./EntityCustomAttributes";
import { api, apiFetch, getEntityTypesList } from "../services/api";
import EnrichReviewDialog, {
  parseJobResult,
  parseJobResultDocument,
} from "./EnrichReviewDialog";
import { isEnrichmentAnchorType } from "../config/enrichmentAnchorTypes";
import EntityAuditTrailSection from "./EntityAuditTrailSection";
import EntityReferenceSourcesSection from "./EntityReferenceSourcesSection";
import {
  getTaxonomyHierarchyConfigByEntityType,
  isTaxonomyHierarchyType,
} from "../utils/taxonomyHierarchyConfigs";

const PROCESSING_STATUS_OPTIONS = ["Processed", "Reviewed", "Approved"];

const RelationshipsLoadingSpinner = ({ className = "w-3 h-3" }) => (
  <svg
    className={`animate-spin shrink-0 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

function getExpectedRelationshipsForSection(
  entityId,
  relationships,
  sectionDef,
  entityIndex
) {
  if (!entityId || !Array.isArray(relationships) || !sectionDef) return [];

  const allowedTypes = new Set(
    (sectionDef.relationshipTypes || [])
      .map((t) => String(t || "").trim().toLowerCase())
      .filter(Boolean)
  );
  const allowedRelatedTypes = new Set(
    (sectionDef.relatedEntityTypes || [])
      .map((t) => normalizeEntityType(t))
      .filter(Boolean)
  );
  const direction = String(sectionDef.direction || "either")
    .toLowerCase()
    .trim();
  const hasTypeFilter = allowedTypes.size > 0;
  const cid = String(entityId).trim().toLowerCase();

  const resolveOtherType = (rel, isSourceDirection) => {
    const otherId = isSourceDirection
      ? rel.target_entity_id
      : rel.source_entity_id;
    const known = entityIndex?.get(String(otherId || ""));
    const hinted = isSourceDirection
      ? rel.target_entity_type
      : rel.source_entity_type;
    return normalizeEntityType(
      known?.type || known?.entity_type || hinted || ""
    );
  };

  const peerLikeFn = (entityId, hintedType) => {
    const known = entityIndex?.get(String(entityId || ""));
    const peerType = normalizeEntityType(
      known?.type || known?.entity_type || hintedType || ""
    );
    const enrichmentCfg = ENRICHMENT_INCOMING_COMPOUND_SECTIONS[sectionDef.key];
    if (enrichmentCfg) {
      return peerType === enrichmentCfg.peerType;
    }
    if (allowedRelatedTypes.size === 0) return true;
    return Boolean(peerType) && allowedRelatedTypes.has(peerType);
  };

  return relationships.filter((rel) => {
    if (!rel) return false;

    if (
      enrichmentIncomingCompoundRelMatches(
        rel,
        sectionDef.key,
        cid,
        peerLikeFn
      )
    ) {
      return true;
    }

    const typeLower = String(rel.type || "").toLowerCase().trim();
    if (hasTypeFilter && !allowedTypes.has(typeLower)) return false;

    let isMatchDirection = false;
    let otherType = "";
    if (direction === "incoming") {
      isMatchDirection = String(rel.target_entity_id || "") === String(entityId);
      if (isMatchDirection) otherType = resolveOtherType(rel, false);
    } else if (direction === "outgoing") {
      isMatchDirection = String(rel.source_entity_id || "") === String(entityId);
      if (isMatchDirection) otherType = resolveOtherType(rel, true);
    } else {
      const touchesEntity =
        String(rel.source_entity_id || "") === String(entityId) ||
        String(rel.target_entity_id || "") === String(entityId);
      isMatchDirection = touchesEntity;
      if (touchesEntity) {
        const isSourceDirection =
          String(rel.source_entity_id || "") === String(entityId);
        otherType = resolveOtherType(rel, isSourceDirection);
      }
    }

    if (!isMatchDirection) return false;
    if (allowedRelatedTypes.size > 0) {
      if (!otherType) return false;
      return allowedRelatedTypes.has(otherType);
    }
    return true;
  });
}

/** Custom-attribute catalog sections (must match admin `entity_type_attributes.section`). */
const CUSTOM_SECTION_LIFECYCLE = "Lifecycle";
const CUSTOM_SECTION_TIME = "TIME Classification";
const CUSTOM_SECTION_EXCLUDED_FROM_REST = [
  CUSTOM_SECTION_LIFECYCLE,
  CUSTOM_SECTION_TIME,
];

const normalizeAliasInput = (entity) => {
  const v = entity.alias_names;
  if (Array.isArray(v)) return v.join(", ");
  if (v === null || v === undefined) return "";
  return String(v);
};

/** Dynamic fields safe for primitive inline edit (objects/arrays stay read-only). */
const filterPrimitiveDynamicFields = (dynamicFieldsList) =>
  (dynamicFieldsList || []).filter(({ value }) => {
    const t = typeof value;
    return t === "string" || t === "number" || t === "boolean";
  });

const buildBasicDraftFromEntity = (displayEntity, dynamicFieldsList) => {
  const dyn = {};
  filterPrimitiveDynamicFields(dynamicFieldsList).forEach(({ key, value }) => {
    dyn[key] =
      typeof value === "boolean"
        ? value
          ? "true"
          : "false"
        : value === null || value === undefined
          ? ""
          : String(value);
  });
  return {
    name: displayEntity.name ?? "",
    type: displayEntity.type || displayEntity.entity_type || "",
    description: displayEntity.description ?? "",
    subtype: displayEntity.subtype ?? "",
    alias_names: normalizeAliasInput(displayEntity),
    processing_status:
      displayEntity.processing_status ||
      displayEntity.status ||
      "Processed",
    dynamic: dyn,
  };
};

const EntityReadOnlyView = ({
  entity,
  relationships = [],
  allEntities = {},
  onClose,
  embedded = false,
  /** V2 architecture explorer: hide internal SC-only interfaces on application views. */
  architectureExplorerView = false,
  /** When true, skip the extra GET /api/entities/:id (parent already loads full payload). */
  disableLiveRefresh = false,
  /** When set, shows an Edit control (e.g. switch parent to edit tab). */
  onRequestEdit,
  /** When set, shows a Delete control (parent handles confirmation and API). */
  onRequestDelete,
  /** Optional controls rendered after Generate Document (e.g. Graph). */
  extraHeaderActions,
  /** Hide the duplicate title/action bar when a parent already shows that chrome. */
  hideHeader = false,
  /** After relationship deletes are persisted from Read (keep parent caches in sync). */
  onRelationshipRemoved,
  /** After section Save persists batch relationship changes (parent may refetch entity). */
  onRelationshipsPersisted,
  /** After inline attribute / custom-attribute saves (parent may refetch full entity). */
  onEntityPersisted,
  /** Parent is still loading relationship rows / full entity payload. */
  relationshipsLoading = false,
  /** Jump to another map item from a relationship row (parent handles navigation stack). */
  onNavigateToRelatedEntity = null,
  /** When set, shows a back control above the header (e.g. return from related-item drill-down). */
  navigationBack = null,
  /** Shell navigation (e.g. jump to Home for Add knowledge prefill). */
  onNavigate = null,
  /** Parent tracks staged edits on this detail view that are not yet saved. */
  onUnsavedEntityDetailChangesChange = null,
  /** @deprecated Use onUnsavedEntityDetailChangesChange */
  onUnsavedRelationshipChangesChange = null,
  /** When set, parent shows a leave confirmation dialog before running the action. */
  requestLeaveWithUnsavedChanges = null,
  /**
   * When false, hide pencils / AI refine / delete / inline edit.
   * Defaults from current user role (Viewers are read-only).
   */
  canEdit = undefined,
}) => {
  const { isMobile } = useDeviceDetection();
  const allowEdit =
    typeof canEdit === "boolean" ? canEdit : userCanContribute();
  const [editingRelationshipSectionKey, setEditingRelationshipSectionKey] =
    React.useState(null);
  const [localEntityCache, setLocalEntityCache] = React.useState({});
  const [neighborsHydrationLoading, setNeighborsHydrationLoading] =
    React.useState(false);
  const neighborsFetchGenRef = React.useRef(0);

  const [editingBasicInfo, setEditingBasicInfo] = React.useState(false);
  const [basicDraft, setBasicDraft] = React.useState(null);
  const basicBaselineRef = React.useRef(null);
  const [savingBasic, setSavingBasic] = React.useState(false);
  const [basicEntityTypeOptions, setBasicEntityTypeOptions] = React.useState([]);
  const [basicSubtypeOptions, setBasicSubtypeOptions] = React.useState([]);
  const [loadingBasicSubtypes, setLoadingBasicSubtypes] = React.useState(false);
  const [proposingDescription, setProposingDescription] = React.useState(false);
  const [descriptionProposal, setDescriptionProposal] = React.useState(null);
  const [descriptionProposalError, setDescriptionProposalError] =
    React.useState(null);
  const descriptionBeforeProposalRef = React.useRef(null);
  const [discoveringAliases, setDiscoveringAliases] = React.useState(false);
  const [aliasProposal, setAliasProposal] = React.useState(null);
  const [aliasProposalError, setAliasProposalError] = React.useState(null);
  const aliasesBeforeProposalRef = React.useRef(null);

  const [refiningSectionKey, setRefiningSectionKey] = React.useState(null);
  const [refineJobId, setRefineJobId] = React.useState(null);
  const [refineStatusMessage, setRefineStatusMessage] = React.useState(null);
  const [refineError, setRefineError] = React.useState(null);
  const [refineReviewOpen, setRefineReviewOpen] = React.useState(false);
  const [refinePreview, setRefinePreview] = React.useState(null);
  const [refineReviewJobId, setRefineReviewJobId] = React.useState(null);
  /** Mobile: hide empty relationship cards behind a single disclosure. */
  const [showEmptyRelationshipSections, setShowEmptyRelationshipSections] =
    React.useState(false);

  const [showPredecessorModal, setShowPredecessorModal] = React.useState(false);
  const [showSuccessorModal, setShowSuccessorModal] = React.useState(false);
  const [timelinePredecessorEntities, setTimelinePredecessorEntities] =
    React.useState([]);
  const [timelineSuccessorEntities, setTimelineSuccessorEntities] =
    React.useState([]);
  const [loadingTimelinePredecessors, setLoadingTimelinePredecessors] =
    React.useState(false);
  const [loadingTimelineSuccessors, setLoadingTimelineSuccessors] =
    React.useState(false);
  const [savingTimelineDeps, setSavingTimelineDeps] = React.useState(false);

  const [editingCustomLifecycle, setEditingCustomLifecycle] =
    React.useState(false);
  const [editingCustomTime, setEditingCustomTime] = React.useState(false);
  const [editingCustomRest, setEditingCustomRest] = React.useState(false);
  const [inlineEntityForm, setInlineEntityForm] = React.useState(() => ({
    custom_attributes: {},
  }));
  const customAttrsBaselineRef = React.useRef(null);
  const [savingCustomAttributes, setSavingCustomAttributes] =
    React.useState(false);
  const [auditRefreshToken, setAuditRefreshToken] = React.useState(0);
  const bumpAuditTrail = React.useCallback(
    () => setAuditRefreshToken((t) => t + 1),
    []
  );
  const [sectionAttrHints, setSectionAttrHints] = React.useState({
    lifecycle: false,
    time: false,
    rest: false,
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await getEntityTypesList(true);
        const filtered = (Array.isArray(raw) ? raw : []).filter(
          (item) => item?.value
        );
        if (!cancelled) setBasicEntityTypeOptions(filtered);
      } catch {
        if (!cancelled) setBasicEntityTypeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!editingBasicInfo || !basicDraft?.type) {
      if (!editingBasicInfo) setBasicSubtypeOptions([]);
      return;
    }
    let cancelled = false;
    const loadSubtypes = async () => {
      setLoadingBasicSubtypes(true);
      try {
        const normalized = normalizeEntityType(basicDraft.type);
        const raw = String(basicDraft.type || "").trim();
        const lookupTypes = [normalized, raw].filter(
          (value, index, all) => value && all.indexOf(value) === index
        );
        let options = [];
        for (const entityType of lookupTypes) {
          const response = await api.getEntitySubtypes(entityType);
          const subtypes = response?.subtypes || [];
          if (Array.isArray(subtypes) && subtypes.length) {
            options = subtypes.filter((s) => s?.is_active !== false);
            break;
          }
        }
        if (!cancelled) setBasicSubtypeOptions(options);
      } catch {
        if (!cancelled) setBasicSubtypeOptions([]);
      } finally {
        if (!cancelled) setLoadingBasicSubtypes(false);
      }
    };
    void loadSubtypes();
    return () => {
      cancelled = true;
    };
  }, [editingBasicInfo, basicDraft?.type]);

  React.useEffect(() => {
    const etName = entity?.entity_type || entity?.type;
    if (!etName) {
      setSectionAttrHints({ lifecycle: false, time: false, rest: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const param = buildEntityTypeAttributesQueryParams(
          etName,
          entity?.entity_type_id
        );
        if (!param) return;
        const response = await apiFetch(
          `/api/admin/entity-type-attributes?${param}`
        );
        if (!response?.ok || cancelled) return;
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const sections = new Set(
          list.map((a) => a.section || "General")
        );
        setSectionAttrHints({
          lifecycle: sections.has(CUSTOM_SECTION_LIFECYCLE),
          time: sections.has(CUSTOM_SECTION_TIME),
          rest: [...sections].some(
            (s) => !CUSTOM_SECTION_EXCLUDED_FROM_REST.includes(s)
          ),
        });
      } catch {
        if (!cancelled) {
          setSectionAttrHints({
            lifecycle: false,
            time: false,
            rest: false,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entity?.id, entity?.entity_type, entity?.type]);

  if (!entity) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-gray-400 text-sm">
          {embedded ? "No item selected. You can close this panel." : "No entity selected. You can close this tab."}
        </div>
      </div>
    );
  }

  // Keep a live snapshot so basic fields reflect the latest saved values,
  // even if the parent/editor still holds a cached entity object.
  const [liveEntity, setLiveEntity] = React.useState(entity);
  React.useEffect(() => {
    setLiveEntity(entity);
  }, [entity]);

  React.useEffect(() => {
    if (disableLiveRefresh) return undefined;
    let isMounted = true;
    const refresh = async () => {
      if (!entity?.id) return;
      try {
        const qs = new URLSearchParams({
          include_relationships: "true",
          include_related_entities: "true",
          includeUnapproved: "true",
        });
        if (architectureExplorerView) {
          qs.set("architecture_explorer", "true");
        }
        const res = await apiFetch(
          `/api/entities/${entity.id}?${qs.toString()}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!isMounted) return;
        if (architectureExplorerView) {
          const filtered = filterApplicationExplorerPayload(
            data,
            data.relationships,
            data.related_entities
          );
          setLiveEntity({
            ...data,
            relationships: filtered.relationships,
            related_entities: filtered.relatedEntities,
          });
        } else {
          setLiveEntity(data);
        }
      } catch {
        // If refresh fails, keep existing liveEntity
      }
    };
    refresh();
    return () => {
      isMounted = false;
    };
  }, [entity?.id, disableLiveRefresh, architectureExplorerView]);

  const displayEntity = liveEntity || entity;

  React.useEffect(() => {
    const ids = parseIdArray(displayEntity?.predecessors);
    if (!ids.length) {
      setTimelinePredecessorEntities([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingTimelinePredecessors(true);
    Promise.all(ids.map((id) => api.getEntity(id)))
      .then((list) => {
        if (!cancelled) setTimelinePredecessorEntities(list.filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setTimelinePredecessorEntities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTimelinePredecessors(false);
      });
    return () => {
      cancelled = true;
    };
  }, [displayEntity?.id, displayEntity?.predecessors]);

  React.useEffect(() => {
    const ids = parseIdArray(displayEntity?.successors);
    if (!ids.length) {
      setTimelineSuccessorEntities([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingTimelineSuccessors(true);
    Promise.all(ids.map((id) => api.getEntity(id)))
      .then((list) => {
        if (!cancelled) setTimelineSuccessorEntities(list.filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setTimelineSuccessorEntities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTimelineSuccessors(false);
      });
    return () => {
      cancelled = true;
    };
  }, [displayEntity?.id, displayEntity?.successors]);

  // Resolve taxonomy / hierarchy parent to a friendly name (parent is a scalar ID).
  const [loadingParentName, setLoadingParentName] = React.useState(false);
  const [showParentPicker, setShowParentPicker] = React.useState(false);
  const [savingParent, setSavingParent] = React.useState(false);
  React.useEffect(() => {
    const parentId = displayEntity?.parent
      ? String(displayEntity.parent).trim()
      : "";
    if (!parentId) {
      setLoadingParentName(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingParentName(true);
    api
      .getEntity(parentId)
      .then((payload) => {
        if (cancelled) return;
        const ent =
          payload?.id && (payload.name !== undefined || payload.type !== undefined)
            ? payload
            : payload?.data && typeof payload.data === "object"
              ? payload.data
              : payload;
        if (!ent?.id) return;
        const idKey = String(ent.id);
        setLocalEntityCache((prev) => ({
          ...prev,
          [idKey]: {
            ...(prev[idKey] || {}),
            ...ent,
            name: ent.name || prev[idKey]?.name,
            type: ent.type || ent.entity_type || prev[idKey]?.type,
            entity_type:
              ent.entity_type || ent.type || prev[idKey]?.entity_type,
          },
        }));
      })
      .catch(() => {
        /* keep ID fallback from resolveEntityRef */
      })
      .finally(() => {
        if (!cancelled) setLoadingParentName(false);
      });

    return () => {
      cancelled = true;
    };
  }, [displayEntity?.id, displayEntity?.parent]);

  const mergedAllEntities = React.useMemo(() => {
    const base = { ...(allEntities || {}), ...(localEntityCache || {}) };
    if (displayEntity?.id) {
      base[String(displayEntity.id)] = {
        ...(base[String(displayEntity.id)] || {}),
        ...displayEntity,
        type: displayEntity.type || displayEntity.entity_type,
        entity_type: displayEntity.entity_type || displayEntity.type,
      };
    }
    const related = displayEntity?.related_entities;
    if (related && typeof related === "object" && !Array.isArray(related)) {
      Object.entries(related).forEach(([id, ent]) => {
        if (ent && id) {
          base[id] = { ...(base[id] || {}), ...ent };
        }
      });
    }
    return base;
  }, [allEntities, displayEntity, localEntityCache]);

  const effectiveRelationships = React.useMemo(() => {
    const fromProps = Array.isArray(relationships) ? relationships : [];
    const fromEntity = Array.isArray(displayEntity?.relationships)
      ? displayEntity.relationships
      : [];
    return fromProps.length > 0 ? fromProps : fromEntity;
  }, [relationships, displayEntity?.relationships]);

  /** Staged deletes per section (memory only until Save — same batch delete as EntityForm). */
  const [pendingRelationshipDeletesBySection, setPendingRelationshipDeletesBySection] =
    React.useState({});
  /** Staged adds per section (merged into resolver until Save — same create payloads as EntityForm). */
  const [pendingAddsBySection, setPendingAddsBySection] = React.useState({});
  /** Staged relationship attribute edits: sectionKey → relationshipId → { api, local }. */
  const [pendingAttributeUpdatesBySection, setPendingAttributeUpdatesBySection] =
    React.useState({});
  const [editingRelationshipAttributes, setEditingRelationshipAttributes] =
    React.useState(null); // { sectionKey, item, relationshipId }
  const [relationshipAttributesSaving, setRelationshipAttributesSaving] =
    React.useState(false);
  const [relationshipSectionErrors, setRelationshipSectionErrors] =
    React.useState({});
  const [relationshipTypeMetaMap, setRelationshipTypeMetaMap] = React.useState(
    {}
  );
  const [savingRelationshipSectionKey, setSavingRelationshipSectionKey] =
    React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const types = await getEntityTypes();
        const meta = {};
        (Array.isArray(types) ? types : []).forEach((type) => {
          const key = String(type.value || "").toLowerCase();
          meta[key] = {
            label: type.label || type.value,
            color: type.color || "#4B5563",
          };
        });
        if (!cancelled) setRelationshipTypeMetaMap(meta);
      } catch {
        if (!cancelled) setRelationshipTypeMetaMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const entityFormForRelationshipStaging = React.useMemo(() => {
    const out = {};
    Object.entries(pendingAddsBySection || {}).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length) out[k] = v;
    });
    return out;
  }, [pendingAddsBySection]);

  const pendingRelationshipDeleteIds = React.useMemo(() => {
    const s = new Set();
    Object.values(pendingRelationshipDeletesBySection).forEach((arr) => {
      (arr || []).forEach((id) => s.add(id));
    });
    return s;
  }, [pendingRelationshipDeletesBySection]);

  const relationshipsForDisplay = React.useMemo(() => {
    if (!pendingRelationshipDeleteIds.size) return effectiveRelationships;
    return (effectiveRelationships || []).filter(
      (r) => r?.id && !pendingRelationshipDeleteIds.has(r.id)
    );
  }, [effectiveRelationships, pendingRelationshipDeleteIds]);

  React.useEffect(() => {
    setEditingRelationshipSectionKey(null);
    setPendingRelationshipDeletesBySection({});
    setPendingAddsBySection({});
    setPendingAttributeUpdatesBySection({});
    setEditingRelationshipAttributes(null);
    setRelationshipSectionErrors({});
    setSavingRelationshipSectionKey(null);
    setShowEmptyRelationshipSections(false);
    setEditingBasicInfo(false);
    setBasicDraft(null);
    basicBaselineRef.current = null;
    setProposingDescription(false);
    setDescriptionProposal(null);
    setDescriptionProposalError(null);
    descriptionBeforeProposalRef.current = null;
    setEditingCustomLifecycle(false);
    setEditingCustomTime(false);
    setEditingCustomRest(false);
    setInlineEntityForm({ custom_attributes: {} });
    customAttrsBaselineRef.current = null;
    setLocalEntityCache({});
    setNeighborsHydrationLoading(false);
  }, [entity?.id]);

  // Normalize custom attributes from live entity snapshot
  let customAttrs = {};
  if (
    displayEntity.custom_attributes !== undefined &&
    displayEntity.custom_attributes !== null
  ) {
    try {
      if (typeof displayEntity.custom_attributes === "string") {
        customAttrs = JSON.parse(displayEntity.custom_attributes) || {};
      } else if (typeof displayEntity.custom_attributes === "object") {
        customAttrs = displayEntity.custom_attributes || {};
      }
    } catch {
      customAttrs = {};
    }
  }

  const baseFields = [
    { key: "name", label: "Name" },
    { key: "type", altKey: "entity_type", label: "Type" },
    { key: "subtype", label: "Subtype" },
    ...(embedded
      ? []
      : [{ key: "processing_status", altKey: "status", label: "Processing status" }]),
    { key: "description", label: "Description" },
    { key: "alias_names", label: "Alias Names" },
    { key: "owner", label: "Owner" },
  ];

  const displayedBaseFields = baseFields
    .map(({ key, altKey, label }) => {
      const value =
        displayEntity[key] !== undefined && displayEntity[key] !== null
          ? displayEntity[key]
          : altKey
            ? displayEntity[altKey]
            : undefined;
      return { key, label, value };
    })
    .filter(({ key, value }) => {
      if (key === "description" || key === "alias_names") return true;
      return !isEmptyValue(value);
    });

  if (embedded) {
    const mapStatus = formatEntityMapStatusLabel(displayEntity);
    if (mapStatus) {
      const subtypeIdx = displayedBaseFields.findIndex(
        (f) => f.label === "Subtype"
      );
      const insertAt = subtypeIdx >= 0 ? subtypeIdx + 1 : displayedBaseFields.length;
      displayedBaseFields.splice(insertAt, 0, {
        label: "Status",
        value: mapStatus,
      });
    }
  }

  // Include any additional non-technical fields dynamically
  const excludedKeys = new Set([
    "id",
    "tenant_id",
    "entity_type_id",
    "created_at",
    "updated_at",
    "confidence",
    "custom_attributes",
    "related_entities",
    "relationships",
    "sourceId",
    "successors",
    "predecessors",
    "children",
    // Hierarchy parent is a technical id; show resolved friendly name instead.
    "parent",
  ]);
  baseFields.forEach(({ key, altKey }) => {
    excludedKeys.add(key);
    if (altKey) excludedKeys.add(altKey);
  });

  if (embedded) {
    excludedKeys.add("approval_status");
    excludedKeys.add("approvalStatus");
    excludedKeys.add("processing_status");
    excludedKeys.add("status");
    excludedKeys.add("awaiting_baseline_approval");
  }

  const dynamicFields = Object.keys(displayEntity)
    .filter((k) => !excludedKeys.has(k))
    .map((k) => ({
      key: k,
      label: formatFieldLabel(k, { mapLanguage: embedded }) || formatLabel(k),
      value: formatFieldDisplayValue(k, displayEntity[k], {
        mapLanguage: embedded,
      }),
    }))
    .filter(({ value }) => !isEmptyValue(value));

  // Helper to resolve entity name/type from ID using related_entities or allEntities
  const resolveEntityRef = (id) => {
    if (!id) return { id: null, name: null, type: null, description: null, status: null };
    const idKey = String(id);

    const related =
      displayEntity.related_entities && displayEntity.related_entities[idKey];
    const fromAll = mergedAllEntities[idKey];

    const rawName = related?.name || fromAll?.name || null;
    // Avoid showing the technical id as if it were the friendly name.
    const name =
      rawName && String(rawName).trim() && String(rawName) !== idKey
        ? rawName
        : rawName || null;
    const type =
      related?.type ||
      related?.entity_type ||
      fromAll?.type ||
      fromAll?.entity_type ||
      null;
    const description =
      related?.description ?? fromAll?.description ?? null;
    const status =
      related?.processing_status ??
      related?.status ??
      fromAll?.processing_status ??
      fromAll?.status ??
      null;

    return { id, name, type, description, status };
  };

  const resolveSectionListNeighbor = (it) => {
    if (!it) return null;
    if (it.other) return it.other;
    const neighborId = it.id || it.target_entity_id || it.source_entity_id;
    if (!isEmptyValue(it.name)) {
      return {
        id: neighborId,
        name: it.name,
        type: it.type || it.entity_type || null,
        description: it.description ?? null,
        status: it.status ?? it.processing_status ?? null,
      };
    }
    return resolveEntityRef(neighborId);
  };

  // Parent / children / predecessors / successors
  const parentRef = displayEntity.parent
    ? resolveEntityRef(displayEntity.parent)
    : null;
  const predecessorRefs = parseIdArray(displayEntity.predecessors).map((id) =>
    resolveEntityRef(id)
  );
  const successorRefs = parseIdArray(displayEntity.successors).map((id) =>
    resolveEntityRef(id)
  );

  const hasHierarchy =
    Boolean(displayEntity.parent) ||
    (parentRef && !isEmptyValue(parentRef.name)) ||
    predecessorRefs.some((p) => !isEmptyValue(p.name)) ||
    successorRefs.some((s) => !isEmptyValue(s.name));

  const timelineTypeName = normalizeEntityType(
    displayEntity?.type || displayEntity?.entity_type || ""
  );
  /** Show hierarchy section when there is content or when typed entity can edit timeline deps inline. */
  const showHierarchySection =
    hasHierarchy || Boolean(timelineTypeName);

  // Relationships grouped like in the edit screen
  const entityType = normalizeEntityType(
    displayEntity.entity_type || displayEntity.type || ""
  );
  const isApplication = isApplicationEntity(displayEntity);

  const sections = React.useMemo(() => {
    const fromCatalog = SECTION_CATALOG[entityType] || [];
    if (fromCatalog.length > 0) return fromCatalog;
    if (isApplication) return SECTION_CATALOG.application || [];
    return [];
  }, [entityType, isApplication]);

  const relationshipsByEntity = React.useMemo(() => {
    const index = new Map();
    (relationshipsForDisplay || []).forEach((rel) => {
      if (!rel) return;
      const sid = String(rel.source_entity_id || "");
      const tid = String(rel.target_entity_id || "");
      if (sid) {
        if (!index.has(sid)) index.set(sid, []);
        index.get(sid).push(rel);
      }
      if (tid) {
        if (!index.has(tid)) index.set(tid, []);
        index.get(tid).push(rel);
      }
    });
    return index;
  }, [relationshipsForDisplay]);

  const entityIdNorm =
    displayEntity?.id !== undefined && displayEntity?.id !== null
      ? String(displayEntity.id)
      : "";
  const entityRelationships =
    (entityIdNorm && relationshipsByEntity.get(entityIdNorm)) || [];

  React.useEffect(() => {
    if (
      !displayEntity?.id ||
      !Array.isArray(effectiveRelationships) ||
      effectiveRelationships.length === 0
    ) {
      setNeighborsHydrationLoading(false);
      return undefined;
    }

    const neighborIds = new Set();
    effectiveRelationships.forEach((rel) => {
      if (!rel) return;
      const isSource = rel.source_entity_id === displayEntity.id;
      const isTarget = rel.target_entity_id === displayEntity.id;
      if (!isSource && !isTarget) return;
      const otherId = isSource ? rel.target_entity_id : rel.source_entity_id;
      if (otherId) neighborIds.add(String(otherId));
    });

    if (neighborIds.size === 0) {
      setNeighborsHydrationLoading(false);
      return undefined;
    }

    const known = new Set();
    Object.keys(mergedAllEntities || {}).forEach((id) => id && known.add(String(id)));

    const missing = Array.from(neighborIds).filter((id) => !known.has(id));
    if (missing.length === 0) {
      setNeighborsHydrationLoading(false);
      return undefined;
    }

    let cancelled = false;
    const fetchGen = ++neighborsFetchGenRef.current;
    setNeighborsHydrationLoading(true);

    const fetchMissingNeighbors = async () => {
      try {
        const additions = {};
        const chunkSize = 40;
        for (let i = 0; i < missing.length; i += chunkSize) {
          const chunk = missing.slice(i, i + chunkSize);
          const response = await apiFetch(
            `/api/entities/batch?ids=${encodeURIComponent(chunk.join(","))}`
          );
          if (!response.ok) continue;
          const data = await response.json();
          const rows = Array.isArray(data?.entities) ? data.entities : [];
          rows.forEach((row) => {
            if (!row?.id) return;
            const canonicalType = normalizeEntityType(row.type || row.entity_type);
            additions[row.id] = {
              id: row.id,
              name: row.name || `Entity ${String(row.id).slice(0, 8)}...`,
              type: canonicalType,
              entity_type: canonicalType,
              description: row.description,
              subtype: row.subtype,
              status: row.status,
            };
          });
        }

        if (cancelled || fetchGen !== neighborsFetchGenRef.current) return;
        if (Object.keys(additions).length > 0) {
          setLocalEntityCache((prev) => ({ ...prev, ...additions }));
        }
      } finally {
        if (!cancelled && fetchGen === neighborsFetchGenRef.current) {
          setNeighborsHydrationLoading(false);
        }
      }
    };

    void fetchMissingNeighbors();
    return () => {
      cancelled = true;
    };
  }, [displayEntity?.id, effectiveRelationships, mergedAllEntities]);

  /** Every catalog section is listed; empty sections show an explicit empty state. */
  const relationshipSectionsWithItems = React.useMemo(() => {
    const entitiesForResolver = Object.keys(mergedAllEntities || {}).map(
      (id) => ({
        id,
        name: mergedAllEntities[id]?.name,
        entity_type:
          mergedAllEntities[id]?.entity_type ||
          mergedAllEntities[id]?.type ||
          "Unknown",
        type:
          mergedAllEntities[id]?.type ||
          mergedAllEntities[id]?.entity_type ||
          "Unknown",
        description: mergedAllEntities[id]?.description,
      })
    );

    return sections.map((sectionDef) => {
      const items =
        resolveSectionItems({
          currentEntity: displayEntity,
          relationships: entityRelationships,
          entities: entitiesForResolver,
          allEntities: mergedAllEntities,
          fetchedEntities: {},
          sectionDef,
          entityForm: entityFormForRelationshipStaging,
          removedItems: {},
          relationshipRules: [],
        }) || [];

      const visibleItems =
        architectureExplorerView &&
        (sectionDef.key === "provided_interfaces" ||
          sectionDef.key === "consumed_interfaces")
          ? filterExplorerInterfaceSectionItems(
              items,
              displayEntity,
              relationshipsForDisplay,
              mergedAllEntities,
              null
            )
          : items;

      const expectedRelationships = getExpectedRelationshipsForSection(
        displayEntity?.id,
        relationshipsForDisplay,
        sectionDef,
        new Map(
          Object.entries(mergedAllEntities || {}).map(([id, ent]) => [
            String(id),
            ent,
          ])
        )
      );

      return {
        key: sectionDef.key,
        label: sectionDef.label,
        sectionDef,
        items: visibleItems,
        expectedRelCount: expectedRelationships.length,
      };
    });
  }, [
    sections,
    displayEntity,
    entityRelationships,
    relationshipsForDisplay,
    mergedAllEntities,
    entityFormForRelationshipStaging,
    architectureExplorerView,
  ]);

  const enterpriseRelationshipSections = React.useMemo(
    () => relationshipSectionsWithItems,
    [relationshipSectionsWithItems]
  );

  const isEnterpriseRelSectionEmpty = React.useCallback(
    (section) => {
      if (!section) return true;
      if ((section.items || []).length > 0) return false;
      if (relationshipsLoading || neighborsHydrationLoading) return false;
      if (editingRelationshipSectionKey === section.key) return false;
      const adds = pendingAddsBySection[section.key] || [];
      const dels = pendingRelationshipDeletesBySection[section.key] || [];
      const attrs = pendingAttributeUpdatesBySection[section.key] || {};
      if (adds.length || dels.length || Object.keys(attrs).length) return false;
      return true;
    },
    [
      relationshipsLoading,
      neighborsHydrationLoading,
      editingRelationshipSectionKey,
      pendingAddsBySection,
      pendingRelationshipDeletesBySection,
      pendingAttributeUpdatesBySection,
    ]
  );

  const emptyEnterpriseRelSectionCount = React.useMemo(
    () =>
      enterpriseRelationshipSections.filter((section) =>
        isEnterpriseRelSectionEmpty(section)
      ).length,
    [enterpriseRelationshipSections, isEnterpriseRelSectionEmpty]
  );

  const unsavedEntityDetailSummary = React.useMemo(() => {
    const summarySections = [];
    const entityName =
      displayEntity?.name || displayEntity?.entity_name || "This item";

    const basicDirty =
      editingBasicInfo &&
      basicDraft &&
      basicBaselineRef.current &&
      JSON.stringify(basicDraft) !== basicBaselineRef.current;

    if (basicDirty && basicDraft && basicBaselineRef.current) {
      const baseline = JSON.parse(basicBaselineRef.current);
      const changedFields = [];
      const trackField = (key, label) => {
        if ((basicDraft[key] ?? "") !== (baseline[key] ?? "")) {
          changedFields.push(label);
        }
      };
      trackField("name", "Name");
      trackField("type", "Type");
      trackField("subtype", "Subtype");
      trackField("description", "Description");
      trackField("alias_names", "Alias names");
      trackField("processing_status", "Processing status");
      const dynamicKeys = new Set([
        ...Object.keys(baseline.dynamic || {}),
        ...Object.keys(basicDraft.dynamic || {}),
      ]);
      dynamicKeys.forEach((key) => {
        const next = basicDraft.dynamic?.[key];
        const prev = baseline.dynamic?.[key];
        if (JSON.stringify(next ?? "") !== JSON.stringify(prev ?? "")) {
          changedFields.push(formatLabel(key));
        }
      });
      if (changedFields.length) {
        summarySections.push({
          key: "basic_info",
          label: "Basic Information",
          kind: "attributes",
          changedFields,
        });
      }
    }

    const customSectionEditingActive =
      editingCustomLifecycle || editingCustomTime || editingCustomRest;
    const customDirty =
      customSectionEditingActive &&
      customAttrsBaselineRef.current &&
      JSON.stringify(inlineEntityForm.custom_attributes || {}) !==
        customAttrsBaselineRef.current;

    if (customDirty && customAttrsBaselineRef.current) {
      const baseline = JSON.parse(customAttrsBaselineRef.current);
      const current = inlineEntityForm.custom_attributes || {};
      const changedFields = Object.keys({ ...baseline, ...current })
        .filter(
          (key) =>
            JSON.stringify(current[key] ?? null) !==
            JSON.stringify(baseline[key] ?? null)
        )
        .map((key) => formatLabel(key));
      let label = "Custom attributes";
      if (editingCustomLifecycle) label = "Lifecycle";
      else if (editingCustomTime) label = "TIME Classification";
      summarySections.push({
        key: "custom_attributes",
        label,
        kind: "attributes",
        changedFields,
      });
    }

    const sectionLabelByKey = {};
    [...enterpriseRelationshipSections].forEach(
      (section) => {
        sectionLabelByKey[section.key] = section.label;
      }
    );
    sections.forEach((section) => {
      if (!sectionLabelByKey[section.key]) {
        sectionLabelByKey[section.key] = section.label;
      }
    });

    const relationshipById = new Map(
      (effectiveRelationships || [])
        .filter((rel) => rel?.id)
        .map((rel) => [rel.id, rel])
    );
    const entityId = String(displayEntity?.id || "");
    const sectionKeys = new Set([
      ...Object.keys(pendingAddsBySection || {}),
      ...Object.keys(pendingRelationshipDeletesBySection || {}),
      ...Object.keys(pendingAttributeUpdatesBySection || {}),
    ]);

    sectionKeys.forEach((sectionKey) => {
      const adds = pendingAddsBySection[sectionKey] || [];
      const deleteIds = pendingRelationshipDeletesBySection[sectionKey] || [];
      const attrUpdates = pendingAttributeUpdatesBySection[sectionKey] || {};
      const updateCount = Object.keys(attrUpdates).length;
      if (!adds.length && !deleteIds.length && !updateCount) return;

      const removeNames = deleteIds
        .map((relationshipId) => {
          const rel = relationshipById.get(relationshipId);
          if (!rel) return "Unknown connection";
          const neighborId =
            String(rel.source_entity_id) === entityId
              ? rel.target_entity_id
              : rel.source_entity_id;
          return resolveEntityRef(neighborId)?.name || "Unknown connection";
        })
        .filter(Boolean);

      summarySections.push({
        key: sectionKey,
        label: sectionLabelByKey[sectionKey] || formatLabel(sectionKey),
        kind: "relationship",
        adds: adds.map((item) => item.name).filter(Boolean),
        removeNames,
        updateCount,
      });
    });

    if (!summarySections.length) {
      return EMPTY_UNSAVED_ENTITY_DETAIL_SUMMARY;
    }

    return {
      hasUnsaved: true,
      entityName,
      sections: summarySections,
    };
  }, [
    enterpriseRelationshipSections,
    sections,
    pendingAddsBySection,
    pendingRelationshipDeletesBySection,
    pendingAttributeUpdatesBySection,
    effectiveRelationships,
    displayEntity?.id,
    displayEntity?.name,
    displayEntity?.entity_name,
    mergedAllEntities,
    localEntityCache,
    editingBasicInfo,
    basicDraft,
    editingCustomLifecycle,
    editingCustomTime,
    editingCustomRest,
    inlineEntityForm,
  ]);

  const requestLeave = React.useCallback(
    (action) => {
      if (!unsavedEntityDetailSummary.hasUnsaved) {
        action?.();
        return;
      }
      if (typeof requestLeaveWithUnsavedChanges === "function") {
        requestLeaveWithUnsavedChanges(action);
        return;
      }
      action?.();
    },
    [requestLeaveWithUnsavedChanges, unsavedEntityDetailSummary.hasUnsaved]
  );

  React.useEffect(() => {
    onUnsavedEntityDetailChangesChange?.(unsavedEntityDetailSummary);
    onUnsavedRelationshipChangesChange?.(unsavedEntityDetailSummary);
  }, [
    unsavedEntityDetailSummary,
    onUnsavedEntityDetailChangesChange,
    onUnsavedRelationshipChangesChange,
  ]);

  React.useEffect(() => {
    if (!unsavedEntityDetailSummary.hasUnsaved) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedEntityDetailSummary.hasUnsaved]);

  const relationshipsPopulateLoading = React.useMemo(() => {
    return Boolean(relationshipsLoading || neighborsHydrationLoading);
  }, [relationshipsLoading, neighborsHydrationLoading]);

  const [userDirectory, setUserDirectory] = React.useState({});

  React.useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      // Always try to resolve the current user (works for non-super users too).
      try {
        const meRes = await apiFetch("/api/auth/me", { method: "GET" });
        if (meRes && meRes.ok) {
          const me = await meRes.json();
          if (!isMounted) return;
          setUserDirectory((prev) => ({
            ...prev,
            [me.id]: me.name || me.email || me.id,
          }));

          if (me?.is_super_user) {
            // Super users can resolve other users globally for better audit readability.
            try {
              const allRes = await apiFetch("/api/admin/all-users", {
                method: "GET",
              });
              if (allRes && allRes.ok) {
                const allUsers = await allRes.json();
                if (!isMounted) return;
                const map = {};
                (Array.isArray(allUsers) ? allUsers : []).forEach((u) => {
                  if (!u?.id) return;
                  map[u.id] = u.name || u.email || u.id;
                });
                setUserDirectory(map);
              }
            } catch {
              // ignore - fallback to current user only
            }
          }
        }
      } catch {
        // ignore - fallback to ids
      }
    };

    loadUsers();
    return () => {
      isMounted = false;
    };
  }, []);

  const clickSectionEdit = (hint) => {
    if (typeof onRequestEdit !== "function") return;
    onRequestEdit(hint);
  };

  const handleRequestClose = () => {
    requestLeave(() => onClose?.());
  };

  const handleNavigateToRelatedEntityGuarded = (relatedRef) => {
    requestLeave(() => onNavigateToRelatedEntity?.(relatedRef));
  };

  const handleNavigationBack = () => {
    requestLeave(() => navigationBack?.onBack?.());
  };

  const toggleRelationshipSectionEdit = (sectionKey) => {
    if (!allowEdit) return;
    setEditingRelationshipSectionKey((prev) =>
      prev === sectionKey ? null : sectionKey
    );
  };

  const stageRelationshipRemoval = (relationshipId, sectionKey) => {
    if (!relationshipId || !sectionKey) return;
    setPendingRelationshipDeletesBySection((prev) => {
      const nextSet = new Set(prev[sectionKey] || []);
      nextSet.add(relationshipId);
      return { ...prev, [sectionKey]: [...nextSet] };
    });
  };

  const unstagePendingRelationshipAdd = (sectionKey, entityId) => {
    if (!sectionKey || !entityId) return;
    setPendingAddsBySection((prev) => {
      const list = prev[sectionKey] || [];
      const filtered = list.filter((x) => x.id !== entityId);
      const next = { ...prev };
      if (filtered.length) next[sectionKey] = filtered;
      else delete next[sectionKey];
      return next;
    });
  };

  const handleRelationshipRowRemove = (
    sectionKey,
    relationshipId,
    entityId
  ) => {
    if (relationshipId) {
      stageRelationshipRemoval(relationshipId, sectionKey);
      return;
    }
    if (entityId) unstagePendingRelationshipAdd(sectionKey, entityId);
  };

  const handleStageRelationshipAdd = (
    sectionKey,
    itemOrItems,
    sectionDef,
    currentItems
  ) => {
    const incomingItems = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
    const itemsToAdd = incomingItems.filter((item) => item?.id);
    if (!itemsToAdd.length || !sectionKey) return;

    const existingIds = new Set((currentItems || []).map((x) => x.id));
    const duplicate = itemsToAdd.find((item) => existingIds.has(item.id));
    if (duplicate) {
      setRelationshipSectionErrors((prev) => ({
        ...prev,
        [sectionKey]: `"${duplicate.name}" is already in this section.`,
      }));
      setTimeout(() => {
        setRelationshipSectionErrors((prev) => {
          const next = { ...prev };
          delete next[sectionKey];
          return next;
        });
      }, 5000);
      return;
    }

    const uniqueItems = itemsToAdd.filter(
      (item, index, arr) => arr.findIndex((x) => x.id === item.id) === index
    );
    const rule = getValidationRule(sectionKey);
    const currentCount = (currentItems || []).length;
    if (
      rule?.maxItems &&
      currentCount + uniqueItems.length > rule.maxItems
    ) {
      setRelationshipSectionErrors((prev) => ({
        ...prev,
        [sectionKey]:
          rule.message ||
          `Only ${rule.maxItems} item${rule.maxItems > 1 ? "s" : ""} allowed.`,
      }));
      setTimeout(() => {
        setRelationshipSectionErrors((prev) => {
          const next = { ...prev };
          delete next[sectionKey];
          return next;
        });
      }, 5000);
      return;
    }
    setRelationshipSectionErrors((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
    uniqueItems.forEach((item) => {
      if (item.name) {
        setLocalEntityCache((prev) => ({
          ...prev,
          [String(item.id)]: {
            ...(prev[String(item.id)] || {}),
            id: item.id,
            name: item.name,
            type: item.type || item.entity_type,
            entity_type: item.entity_type || item.type,
          },
        }));
      }
    });
    setPendingAddsBySection((prev) => ({
      ...prev,
      [sectionKey]: [...(prev[sectionKey] || []), ...uniqueItems],
    }));
  };

  const handleCancelSectionRelationships = (sectionKey) => {
    setPendingRelationshipDeletesBySection((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
    setPendingAddsBySection((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
    setPendingAttributeUpdatesBySection((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
    setRelationshipSectionErrors((prev) => {
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
  };

  const handleStageRelationshipAttributeUpdate = (
    sectionKey,
    item,
    relationshipId,
    { api: apiPayload, local }
  ) => {
    if (!sectionKey || !item) return;
    // Staged add (not persisted yet) — patch the pending add row.
    if (!relationshipId || String(relationshipId).startsWith("pending:")) {
      setPendingAddsBySection((prev) => ({
        ...prev,
        [sectionKey]: (prev[sectionKey] || []).map((row) =>
          row?.id === item.id ? { ...row, ...local } : row
        ),
      }));
      return;
    }
    setPendingAttributeUpdatesBySection((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        [relationshipId]: { api: apiPayload, local },
      },
    }));
  };

  const handleSaveSectionRelationships = async (sectionKey) => {
    const deleteIds = [
      ...new Set(pendingRelationshipDeletesBySection[sectionKey] || []),
    ];
    const adds = pendingAddsBySection[sectionKey] || [];
    const attrUpdatesMap = pendingAttributeUpdatesBySection[sectionKey] || {};
    const updates = Object.entries(attrUpdatesMap).map(([id, payload]) => ({
      id,
      updates: payload?.api || {},
    }));
    const catalogEntityType =
      displayEntity.type || displayEntity.entity_type || "";
    const creates = buildRelationshipCreatesForSection({
      sectionKey,
      currentEntityId: displayEntity.id,
      relatedEntities: adds,
      catalogEntityType,
    });

    if (!deleteIds.length && !creates.length && !updates.length) return;

    const clearPendingForSection = () => {
      setPendingRelationshipDeletesBySection((prev) => {
        const next = { ...prev };
        delete next[sectionKey];
        return next;
      });
      setPendingAddsBySection((prev) => {
        const next = { ...prev };
        delete next[sectionKey];
        return next;
      });
      setPendingAttributeUpdatesBySection((prev) => {
        const next = { ...prev };
        delete next[sectionKey];
        return next;
      });
    };

    const applySuccessfulSave = (result, idsDeleted) => {
      clearPendingForSection();
      idsDeleted.forEach((id) => onRelationshipRemoved?.(id));
      const deletedIfaces = result?.results?.deleted_interfaces || [];
      if (
        deletedIfaces.length &&
        deletedIfaces.includes(String(displayEntity?.id || ""))
      ) {
        onRelationshipsPersisted?.({ entityDeleted: true });
      } else {
        onRelationshipsPersisted?.();
      }
      bumpAuditTrail();
    };

    const confirmCascadeDeleteInterfaces = (incompleteInterfaces) => {
      const names = (incompleteInterfaces || [])
        .map((row) => row?.name || row?.id)
        .filter(Boolean);
      const label =
        names.length === 1
          ? `"${names[0]}"`
          : names.length > 1
            ? `${names.length} interfaces`
            : "this interface";
      return window.confirm(
        `Removing this connection would leave ${label} without both a provider and a consumer application.\n\n` +
          `Delete ${label} entirely? This cannot be undone.`
      );
    };

    setSavingRelationshipSectionKey(sectionKey);
    try {
      const payload = {};
      if (deleteIds.length) payload.delete = deleteIds;
      if (creates.length) payload.create = creates;
      if (updates.length) payload.update = updates;
      try {
        const result = await api.batchUpdateRelationships(payload);
        applySuccessfulSave(result, deleteIds);
      } catch (err) {
        const incomplete = err?.incompleteInterfaces || [];
        const isEndpointInvariant =
          err?.field === "interface_endpoints" ||
          /interface .* incomplete|must have at least one provider/i.test(
            String(err?.message || "")
          );
        if (
          isEndpointInvariant &&
          deleteIds.length &&
          confirmCascadeDeleteInterfaces(incomplete)
        ) {
          const result = await api.batchUpdateRelationships({
            ...payload,
            cascade_delete_incomplete_interfaces: true,
          });
          applySuccessfulSave(result, deleteIds);
        } else {
          setRelationshipSectionErrors((prev) => ({
            ...prev,
            [sectionKey]:
              err?.message || "Could not save relationship changes.",
          }));
          window.alert(err?.message || "Could not save relationship changes.");
        }
      }
    } catch (err) {
      window.alert(err?.message || "Could not save relationship changes.");
    } finally {
      setSavingRelationshipSectionKey(null);
    }
  };

  const beginBasicInfoEdit = () => {
    if (!allowEdit) return;
    const draft = buildBasicDraftFromEntity(displayEntity, dynamicFields);
    basicBaselineRef.current = JSON.stringify(draft);
    setBasicDraft(draft);
    setEditingBasicInfo(true);
  };

  const cancelBasicInfoEdit = () => {
    setEditingBasicInfo(false);
    setBasicDraft(null);
    basicBaselineRef.current = null;
    setDescriptionProposal(null);
    setDescriptionProposalError(null);
    descriptionBeforeProposalRef.current = null;
  };

  const clearDescriptionProposal = () => {
    setDescriptionProposal(null);
    setDescriptionProposalError(null);
    descriptionBeforeProposalRef.current = null;
  };

  const handleProposeDescription = async () => {
    if (!displayEntity?.id || proposingDescription) return;
    const current = editingBasicInfo
      ? basicDraft?.description ?? ""
      : displayEntity.description ?? "";
    descriptionBeforeProposalRef.current = current;
    setProposingDescription(true);
    setDescriptionProposalError(null);
    try {
      const res = await api.proposeEntityDescription(displayEntity.id, {
        currentDescription: current,
      });
      const proposed = (res?.proposed_description || "").trim();
      if (!proposed) {
        throw new Error("No description was proposed");
      }
      setDescriptionProposal({
        text: proposed,
        rationale: res?.rationale || null,
        mode: res?.mode || (String(current).trim() ? "refine" : "generate"),
        fragmentCount: res?.fragment_count || 0,
      });
      if (editingBasicInfo) {
        setBasicDraft((d) => (d ? { ...d, description: proposed } : d));
      }
    } catch (err) {
      setDescriptionProposal(null);
      setDescriptionProposalError(
        err?.message || "Could not propose a description"
      );
    } finally {
      setProposingDescription(false);
    }
  };

  const handleDiscardDescriptionProposal = () => {
    const prior = descriptionBeforeProposalRef.current;
    if (editingBasicInfo && prior !== null) {
      setBasicDraft((d) => (d ? { ...d, description: prior } : d));
    }
    clearDescriptionProposal();
  };

  const clearAliasProposal = () => {
    setAliasProposal(null);
    setAliasProposalError(null);
    aliasesBeforeProposalRef.current = null;
  };

  const mergeAliasStrings = (existing, additions) => {
    const split = (raw) =>
      String(raw || "")
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const name of [...split(existing), ...split(additions)]) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    return out.join(", ");
  };

  const handleDiscoverAliases = async () => {
    if (!displayEntity?.id || discoveringAliases) return;
    const current = editingBasicInfo
      ? basicDraft?.alias_names ?? ""
      : displayEntity.alias_names ?? "";
    aliasesBeforeProposalRef.current = current;
    setDiscoveringAliases(true);
    setAliasProposalError(null);
    try {
      const res = await api.discoverEntityAliases(displayEntity.id, {
        currentAliasNames: current,
      });
      const proposedList = Array.isArray(res?.aliases) ? res.aliases : [];
      const proposedText = String(
        res?.alias_names_text || proposedList.join(", ")
      ).trim();
      if (!proposedText && !proposedList.length) {
        throw new Error("No aliases were proposed");
      }
      const merged = mergeAliasStrings(current, proposedText);
      setAliasProposal({
        aliases: proposedList,
        text: proposedText,
        merged,
        rationale: res?.rationale || null,
        fragmentCount: res?.fragment_count || 0,
      });
      if (editingBasicInfo) {
        setBasicDraft((d) => (d ? { ...d, alias_names: merged } : d));
      }
    } catch (err) {
      setAliasProposal(null);
      setAliasProposalError(err?.message || "Could not discover aliases");
    } finally {
      setDiscoveringAliases(false);
    }
  };

  const handleDiscardAliasProposal = () => {
    const prior = aliasesBeforeProposalRef.current;
    if (editingBasicInfo && prior !== null) {
      setBasicDraft((d) => (d ? { ...d, alias_names: prior } : d));
    }
    clearAliasProposal();
  };

  const handleApplyDescriptionProposal = async () => {
    if (!displayEntity?.id) return;
    const proposed = String(descriptionProposal?.text || "").trim();
    if (!proposed) return;
    if (editingBasicInfo) {
      setBasicDraft((d) => (d ? { ...d, description: proposed } : d));
      clearDescriptionProposal();
      return;
    }
    setSavingBasic(true);
    try {
      const res = await api.updateEntity(displayEntity.id, {
        description: proposed,
      });
      const updated =
        res?.entity && typeof res.entity === "object"
          ? res.entity
          : { ...displayEntity, description: proposed };
      setLiveEntity(updated);
      onEntityPersisted?.();
      clearDescriptionProposal();
      bumpAuditTrail();
    } catch (err) {
      window.alert(err?.message || "Could not save description.");
    } finally {
      setSavingBasic(false);
    }
  };

  const canRefineFromKnowledge = React.useMemo(() => {
    if (!allowEdit) return false;
    const t =
      displayEntity?.type ||
      displayEntity?.entity_type ||
      displayEntity?.entityType;
    return Boolean(displayEntity?.id && isEnrichmentAnchorType(t));
  }, [
    allowEdit,
    displayEntity?.id,
    displayEntity?.type,
    displayEntity?.entity_type,
    displayEntity?.entityType,
  ]);

  const refiningBusy = Boolean(refineJobId) || Boolean(refiningSectionKey);

  const handleRefineFromKnowledge = React.useCallback(
    async (sectionKeys = null) => {
      if (!displayEntity?.id || refiningBusy) return;
      const keyLabel =
        Array.isArray(sectionKeys) && sectionKeys.length === 1
          ? sectionKeys[0]
          : "__all__";
      setRefiningSectionKey(keyLabel);
      setRefineError(null);
      setRefineStatusMessage(
        Array.isArray(sectionKeys) && sectionKeys.length === 1
          ? `Getting AI recommendations for ${String(sectionKeys[0]).replace(/_/g, " ")}…`
          : "Getting AI recommendations for all sections…"
      );
      try {
        const res = await api.refineEntityFromKnowledge(displayEntity.id, {
          sectionKeys,
        });
        const jid = String(res?.job_id || "").trim();
        if (!jid) throw new Error("No job id returned");
        setRefineJobId(jid);
      } catch (err) {
        setRefineJobId(null);
        setRefiningSectionKey(null);
        setRefineStatusMessage(null);
        setRefineError(err?.message || "Could not start AI recommendations");
      }
    },
    [displayEntity?.id, refiningBusy]
  );

  React.useEffect(() => {
    if (!refineJobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const job = await api.getJobStatus(refineJobId);
        if (cancelled || !job) return;
        const st = String(job.status || "").toLowerCase();
        if (st === "completed") {
          const doc = parseJobResultDocument(job);
          const preview = parseJobResult(job);
          const hasDeltas = Boolean(doc?.has_deltas);
          const mode = String(doc?.mode || "").toLowerCase();
          const message =
            String(doc?.message || "").trim() ||
            "Nothing new, obsolete, or changed for this section.";
          setRefineJobId(null);
          setRefiningSectionKey(null);
          if (mode === "no_knowledge" || mode === "no_sections" || !hasDeltas) {
            setRefinePreview(null);
            setRefineReviewOpen(false);
            setRefineStatusMessage(message);
            return;
          }
          if (preview) {
            setRefineReviewJobId(String(job.id || refineJobId));
            setRefinePreview(preview);
            setRefineReviewOpen(true);
            setRefineStatusMessage(null);
          } else {
            setRefineStatusMessage(message);
          }
          return;
        }
        if (st === "failed" || st === "cancelled") {
          setRefineJobId(null);
          setRefiningSectionKey(null);
          setRefineStatusMessage(null);
          setRefineError(
            String(job.error_message || job.error || "Refine job failed").trim()
          );
        }
      } catch (e) {
        console.warn("Refine job poll failed:", e);
      }
    };
    poll();
    const interval = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [refineJobId]);

  const handleRefinePreviewPatch = React.useCallback((mutator) => {
    setRefinePreview((prev) => {
      if (!prev) return prev;
      return mutator(prev);
    });
  }, []);

  const handleRefineRelationshipsApplied = React.useCallback(() => {
    onRelationshipsPersisted?.();
    bumpAuditTrail();
  }, [onRelationshipsPersisted, bumpAuditTrail]);

  const basicInfoDirty =
    editingBasicInfo &&
    basicDraft &&
    JSON.stringify(basicDraft) !== basicBaselineRef.current;

  const handleSaveBasicInfo = async () => {
    if (!displayEntity?.id || !basicDraft) return;

    const buildPayload = () => {
      const payload = {
        name: basicDraft.name.trim(),
        description: basicDraft.description ?? "",
        subtype: basicDraft.subtype || null,
        alias_names: basicDraft.alias_names?.trim() || null,
        processing_status: basicDraft.processing_status || "Processed",
      };
      const originalType = String(
        displayEntity.type || displayEntity.entity_type || ""
      ).trim();
      const newType = String(basicDraft.type || "").trim();
      if (newType && newType !== originalType) {
        payload.type = newType;
      }
      Object.keys(basicDraft.dynamic || {}).forEach((key) => {
        const raw = basicDraft.dynamic[key];
        const orig = displayEntity[key];
        if (typeof orig === "number") {
          if (raw === "" || raw === null || raw === undefined) {
            payload[key] = null;
          } else {
            const n = Number(raw);
            payload[key] = Number.isNaN(n) ? null : n;
          }
        } else if (typeof orig === "boolean") {
          payload[key] = raw === true || raw === "true";
        } else {
          payload[key] = raw;
        }
      });
      return payload;
    };

    const finishSuccess = (ent) => {
      if (ent && typeof ent === "object") setLiveEntity(ent);
      onEntityPersisted?.();
      bumpAuditTrail();
      setEditingBasicInfo(false);
      setBasicDraft(null);
      basicBaselineRef.current = null;
    };

    const extractNestedDetail = (err) => {
      let d = err?.detail;
      if (d && typeof d === "object" && d.detail) d = d.detail;
      return d;
    };

    const looksLikeTypeChangeConfirmation = (err, payloadHasType) => {
      if (!payloadHasType) return false;
      const nested = extractNestedDetail(err);
      const msg = String(err?.message || "");
      if (
        nested &&
        typeof nested === "object" &&
        (nested.requires_confirmation === true ||
          (Array.isArray(nested.invalid_relationships) &&
            nested.invalid_relationships.length > 0))
      ) {
        return true;
      }
      if (
        msg.includes("would make") &&
        msg.includes("relationship") &&
        msg.includes("invalid")
      ) {
        return true;
      }
      return false;
    };

    setSavingBasic(true);
    try {
      const payload = buildPayload();
      const res = await api.updateEntity(displayEntity.id, payload);
      const ent = res?.entity || res;
      finishSuccess(ent);
    } catch (err) {
      const payload = buildPayload();
      const hasTypeChange = Boolean(payload.type);
      if (
        looksLikeTypeChangeConfirmation(err, hasTypeChange) &&
        !payload.remove_invalid_relationships
      ) {
        const nested = extractNestedDetail(err);
        const msg =
          (nested && typeof nested === "object" && nested.message) ||
          err?.message ||
          "Changing the entity type may invalidate existing relationships. Remove invalid relationships and continue?";
        const proceed = window.confirm(msg);
        if (proceed) {
          try {
            const res = await api.updateEntity(displayEntity.id, {
              ...payload,
              remove_invalid_relationships: true,
            });
            const ent = res?.entity || res;
            finishSuccess(ent);
          } catch (err2) {
            window.alert(err2?.message || "Could not save changes.");
          }
        }
      } else {
        window.alert(err?.message || "Could not save changes.");
      }
    } finally {
      setSavingBasic(false);
    }
  };

  const cancelAnyCustomSectionEdit = () => {
    setEditingCustomLifecycle(false);
    setEditingCustomTime(false);
    setEditingCustomRest(false);
    setInlineEntityForm({ custom_attributes: {} });
    customAttrsBaselineRef.current = null;
  };

  const seedInlineCustomFormAndBaseline = () => {
    const snapshot = JSON.stringify(customAttrs || {});
    customAttrsBaselineRef.current = snapshot;
    setInlineEntityForm({ custom_attributes: { ...customAttrs } });
  };

  const beginLifecycleCustomEdit = () => {
    setEditingCustomTime(false);
    setEditingCustomRest(false);
    seedInlineCustomFormAndBaseline();
    setEditingCustomLifecycle(true);
  };

  const beginTimeClassificationCustomEdit = () => {
    setEditingCustomLifecycle(false);
    setEditingCustomRest(false);
    seedInlineCustomFormAndBaseline();
    setEditingCustomTime(true);
  };

  const beginRestCustomAttributesEdit = () => {
    setEditingCustomLifecycle(false);
    setEditingCustomTime(false);
    seedInlineCustomFormAndBaseline();
    setEditingCustomRest(true);
  };

  const customSectionEditingActive =
    editingCustomLifecycle || editingCustomTime || editingCustomRest;

  /** Mild mobile density for attribute / relationship sections (keeps side breathing room). */
  const sectionTitleRowClass = isMobile
    ? "flex items-center justify-between gap-2 mb-2 border-b border-gray-700 pb-1 flex-wrap"
    : "flex items-center justify-between gap-2 mb-3 border-b border-gray-700 pb-1 flex-wrap";
  const fieldGridClass = isMobile
    ? "grid grid-cols-1 md:grid-cols-2 gap-2.5"
    : "grid grid-cols-1 md:grid-cols-2 gap-4";
  const fieldCardClass = isMobile
    ? "bg-gray-800 rounded-lg border border-gray-700 px-2.5 py-1.5"
    : "bg-gray-800 rounded-lg border border-gray-700 px-3 py-2";
  const sectionStackClass = isMobile ? "space-y-2" : "space-y-3";

  const customAttributesDirty =
    customSectionEditingActive &&
    JSON.stringify(inlineEntityForm.custom_attributes || {}) !==
      customAttrsBaselineRef.current;

  const handleSaveCustomAttributes = async () => {
    if (!displayEntity?.id) return;
    setSavingCustomAttributes(true);
    try {
      const ca = inlineEntityForm.custom_attributes || {};
      const res = await api.updateEntity(displayEntity.id, {
        custom_attributes:
          ca && typeof ca === "object" && !Array.isArray(ca) ? ca : {},
      });
      const ent = res?.entity || res;
      if (ent && typeof ent === "object") setLiveEntity(ent);
      onEntityPersisted?.();
      bumpAuditTrail();
      setEditingCustomLifecycle(false);
      setEditingCustomTime(false);
      setEditingCustomRest(false);
      customAttrsBaselineRef.current = null;
    } catch (err) {
      window.alert(err?.message || "Could not save custom attributes.");
    } finally {
      setSavingCustomAttributes(false);
    }
  };

  const persistTimelineField = async (field, ids) => {
    if (!displayEntity?.id) return;
    setSavingTimelineDeps(true);
    try {
      const res = await api.updateEntity(displayEntity.id, { [field]: ids });
      const ent = res?.entity || res;
      if (ent && typeof ent === "object") setLiveEntity(ent);
      onEntityPersisted?.();
      bumpAuditTrail();
    } catch (err) {
      window.alert(err?.message || `Could not update ${field}.`);
    } finally {
      setSavingTimelineDeps(false);
    }
  };

  const entityTypeForTaxonomy = normalizeEntityType(
    displayEntity?.type || displayEntity?.entity_type || ""
  );
  const isTaxonomyEntity = isTaxonomyHierarchyType(entityTypeForTaxonomy);
  const taxonomyCfg = getTaxonomyHierarchyConfigByEntityType(
    entityTypeForTaxonomy
  );

  const persistParent = async (parentId, node = null) => {
    if (!displayEntity?.id) return;
    setSavingParent(true);
    try {
      const nextParent = parentId || null;
      const res = await api.updateEntity(displayEntity.id, {
        parent: nextParent,
      });
      const ent = res?.entity || res;
      if (ent && typeof ent === "object") {
        setLiveEntity(ent);
      } else {
        setLiveEntity((prev) =>
          prev ? { ...prev, parent: nextParent } : prev
        );
      }
      if (nextParent && node?.name) {
        setLocalEntityCache((prev) => ({
          ...prev,
          [String(node.id)]: {
            ...(prev[String(node.id)] || {}),
            id: node.id,
            name: node.name,
            description: node.description || null,
            type: entityTypeForTaxonomy,
            entity_type: entityTypeForTaxonomy,
          },
        }));
      }
      onEntityPersisted?.();
      bumpAuditTrail();
    } catch (err) {
      window.alert(err?.message || "Could not update parent.");
    } finally {
      setSavingParent(false);
    }
  };

  const inputCls =
    "w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500";

  const customInlineKeySuffix = editingCustomLifecycle
    ? "lc"
    : editingCustomTime
      ? "tm"
      : editingCustomRest
        ? "rest"
        : "none";

  return (
    <div
      className={
        embedded
          ? "flex flex-col rounded-lg bg-gray-900"
          : "min-h-screen bg-gray-900 flex flex-col"
      }
    >
      {navigationBack && !hideHeader ? (
        <div className="border-b border-gray-700 bg-gray-800/80 px-4 py-2">
          <button
            type="button"
            onClick={handleNavigationBack}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            {navigationBack.label}
          </button>
        </div>
      ) : null}
      {/* Header */}
      {!hideHeader ? (
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">
            {displayEntity.name || "Entity Details"}
          </h1>
          <p className="text-sm text-gray-400 flex flex-wrap items-center gap-2">
            <span>
              {displayEntity.type ||
                displayEntity.entity_type ||
                "Unknown type"}
            </span>
            {embedded && formatEntityMapStatusLabel(displayEntity) ? (
              <span className="inline-flex rounded-full bg-gray-700/80 px-2 py-0.5 text-[11px] font-medium text-gray-200">
                {formatEntityMapStatusLabel(displayEntity)}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(displayEntity.type === "application" ||
            displayEntity.entity_type === "application" ||
            displayEntity.type === "ApplicationComponent" ||
            displayEntity.entity_type === "ApplicationComponent") && (
            <GenerateDocumentButton
              entityId={displayEntity.id}
              entityName={displayEntity.name}
            />
          )}
          {extraHeaderActions}
          {allowEdit && typeof onRequestEdit === "function" && (
            <button
              type="button"
              onClick={onRequestEdit}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors inline-flex items-center gap-1.5"
              aria-label={embedded ? "Edit item" : "Edit entity"}
              title={embedded ? "Edit item" : "Edit entity"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden
              >
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit
            </button>
          )}
          {allowEdit &&
          typeof onRequestDelete === "function" &&
          displayEntity?.id ? (
            <button
              type="button"
              onClick={() => onRequestDelete(displayEntity)}
              className="px-3 py-1.5 text-sm bg-rose-700 hover:bg-rose-600 text-white rounded-lg transition-colors inline-flex items-center gap-1.5"
              aria-label={embedded ? "Delete item" : "Delete entity"}
              title={embedded ? "Delete item" : "Delete entity"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 00-.629.74v.018c0 .614.221 1.205.625 1.688.404.483.98.84 1.625 1.02v10.614a2.25 2.25 0 002.25 2.25h7.5a2.25 2.25 0 002.25-2.25V7.318c.645-.18 1.221-.537 1.625-1.02a1.75 1.75 0 00.625-1.688v-.018a.75.75 0 00-.629-.74c-.781-.122-1.57-.22-2.365-.298V3.75A2.75 2.75 0 0011.25 1h-2.5zM8 3.75c0-.69.56-1.25 1.25-1.25h1.5c.69 0 1.25.56 1.25 1.25v.443a47.06 47.06 0 00-4 0V3.75zm7.75 4.364v10.614a.75.75 0 01-.75.75h-7.5a.75.75 0 01-.75-.75V8.114a47.06 47.06 0 004 0z"
                  clipRule="evenodd"
                />
              </svg>
              Delete
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleRequestClose}
            className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      ) : null}

      {/* Content — embedded: parent pane scrolls; standalone: internal scroll */}
      <div
        className={
          embedded
            ? isMobile
              ? "px-2.5 py-2 space-y-3"
              : "px-6 py-4 space-y-8"
            : isMobile
              ? "flex-1 overflow-x-hidden overflow-y-auto px-3 py-3 space-y-4"
              : "flex-1 overflow-x-hidden overflow-y-auto px-6 py-4 space-y-8"
        }
      >
        {/* Basic Information — optional inline edit */}
        <section>
          <div className={sectionTitleRowClass}>
            <h2 className="text-lg font-semibold text-white">Basic Information</h2>
            <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
              {editingBasicInfo && basicInfoDirty ? (
                <>
                  <button
                    type="button"
                    disabled={savingBasic}
                    className="text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-3 py-1.5 rounded-md border border-gray-600"
                    onClick={cancelBasicInfoEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={savingBasic}
                    className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-md border border-blue-500/80"
                    onClick={handleSaveBasicInfo}
                  >
                    {savingBasic ? "Saving…" : "Save"}
                  </button>
                </>
              ) : null}
              {allowEdit ? (
              <button
                type="button"
                className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-400 hover:bg-gray-700/80 transition-colors"
                aria-label={
                  editingBasicInfo ? "Stop editing basic information" : "Edit basic information"
                }
                title={
                  editingBasicInfo ? "Done editing (no tab switch)" : "Edit basic information"
                }
                onClick={() =>
                  editingBasicInfo ? cancelBasicInfoEdit() : beginBasicInfoEdit()
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
              ) : null}
              {allowEdit && typeof onRequestEdit === "function" ? (
                <button
                  type="button"
                  className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded border border-transparent hover:border-gray-600"
                  onClick={() => clickSectionEdit({ section: "basic" })}
                  title="Open full editor tab"
                >
                  Full edit
                </button>
              ) : null}
            </div>
          </div>
          {editingBasicInfo && basicDraft ? (
            <div className={fieldGridClass}>
              <div className={`${fieldCardClass} md:col-span-2`}>
                <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">
                  Name
                </label>
                <input
                  type="text"
                  value={basicDraft.name}
                  onChange={(e) =>
                    setBasicDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  className={inputCls}
                />
              </div>
              <div className={fieldCardClass}>
                <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">
                  Type
                </label>
                <select
                  value={basicDraft.type || ""}
                  onChange={(e) => {
                    const next = e.target.value;
                    setBasicDraft((d) => ({
                      ...d,
                      type: next,
                      subtype: next === d.type ? d.subtype : "",
                    }));
                  }}
                  className={inputCls}
                >
                  {basicDraft.type &&
                  !basicEntityTypeOptions.some(
                    (o) => String(o.value) === String(basicDraft.type)
                  ) ? (
                    <option value={basicDraft.type}>{basicDraft.type}</option>
                  ) : null}
                  {basicEntityTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label || opt.value}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Changing type may invalidate relationships; you can confirm removal on save when
                  prompted.
                </p>
              </div>
              <div className={fieldCardClass}>
                <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">
                  Subtype
                </label>
                <select
                  value={basicDraft.subtype || ""}
                  onChange={(e) =>
                    setBasicDraft((d) => ({ ...d, subtype: e.target.value }))
                  }
                  disabled={loadingBasicSubtypes}
                  className={inputCls}
                >
                  <option value="">
                    {loadingBasicSubtypes ? "Loading subtypes…" : "Select subtype…"}
                  </option>
                  {basicDraft.subtype &&
                  !basicSubtypeOptions.some(
                    (s) =>
                      String(s.subtype_name || "") === String(basicDraft.subtype)
                  ) ? (
                    <option value={basicDraft.subtype}>{basicDraft.subtype}</option>
                  ) : null}
                  {basicSubtypeOptions.map((subtype) => (
                    <option
                      key={subtype.id || subtype.subtype_name}
                      value={subtype.subtype_name}
                    >
                      {subtype.subtype_name}
                    </option>
                  ))}
                </select>
              </div>
              {!embedded ? (
              <div className={fieldCardClass}>
                <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">
                  Processing status
                </label>
                <select
                  value={basicDraft.processing_status}
                  onChange={(e) =>
                    setBasicDraft((d) => ({
                      ...d,
                      processing_status: e.target.value,
                    }))
                  }
                  className={inputCls}
                >
                  {!PROCESSING_STATUS_OPTIONS.includes(
                    basicDraft.processing_status
                  ) &&
                  basicDraft.processing_status ? (
                    <option value={basicDraft.processing_status}>
                      {basicDraft.processing_status}
                    </option>
                  ) : null}
                  {PROCESSING_STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              ) : null}
              <div className={`${fieldCardClass} md:col-span-2`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-xs uppercase tracking-wide text-gray-400 block">
                    Alias names
                  </label>
                  <button
                    type="button"
                    disabled={discoveringAliases || savingBasic || !displayEntity?.id}
                    onClick={() => void handleDiscoverAliases()}
                    className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {discoveringAliases ? "Working…" : "Discover aliases"}
                  </button>
                </div>
                <input
                  type="text"
                  value={basicDraft.alias_names}
                  onChange={(e) => {
                    setBasicDraft((d) => ({
                      ...d,
                      alias_names: e.target.value,
                    }));
                    if (aliasProposal) {
                      setAliasProposal((prev) =>
                        prev ? { ...prev, merged: e.target.value } : prev
                      );
                    }
                  }}
                  placeholder="Comma-separated"
                  className={inputCls}
                />
                {aliasProposalError ? (
                  <p className="mt-1 text-xs text-amber-400">{aliasProposalError}</p>
                ) : null}
                {aliasProposal ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-400">
                      Draft from linked knowledge
                      {aliasProposal.fragmentCount
                        ? ` (${aliasProposal.fragmentCount} snippets)`
                        : ""}
                      . Edit as needed, then save Basic Info — or discard.
                    </span>
                    <button
                      type="button"
                      onClick={handleDiscardAliasProposal}
                      className="px-2 py-0.5 text-xs font-medium text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
                    >
                      Discard
                    </button>
                  </div>
                ) : null}
              </div>
              <div className={`${fieldCardClass} md:col-span-2`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-xs uppercase tracking-wide text-gray-400 block">
                    Description
                  </label>
                  <button
                    type="button"
                    disabled={proposingDescription || savingBasic || !displayEntity?.id}
                    onClick={handleProposeDescription}
                    className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {proposingDescription
                      ? "Working…"
                      : String(basicDraft.description || "").trim()
                        ? "Propose description"
                        : "Generate description"}
                  </button>
                </div>
                <textarea
                  value={basicDraft.description}
                  onChange={(e) => {
                    const next = e.target.value;
                    setBasicDraft((d) => ({
                      ...d,
                      description: next,
                    }));
                    setDescriptionProposal((prev) =>
                      prev ? { ...prev, text: next } : prev
                    );
                  }}
                  rows={4}
                  className={inputCls}
                />
                {descriptionProposalError ? (
                  <p className="mt-1 text-xs text-amber-400">
                    {descriptionProposalError}
                  </p>
                ) : null}
                {descriptionProposal ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-400">
                      Editable draft from knowledge
                      {descriptionProposal.fragmentCount
                        ? ` (${descriptionProposal.fragmentCount} snippets)`
                        : ""}
                      . Edit as needed, then save Basic Info — or discard.
                    </span>
                    <button
                      type="button"
                      onClick={handleDiscardDescriptionProposal}
                      className="px-2 py-0.5 text-xs font-medium text-gray-300 border border-gray-600 rounded hover:bg-gray-700"
                    >
                      Discard
                    </button>
                  </div>
                ) : null}
              </div>
              {Object.keys(basicDraft.dynamic || {}).map((key) => {
                const df = dynamicFields.find((x) => x.key === key);
                const label = df?.label || formatLabel(key);
                return (
                  <div
                    key={key}
                    className={fieldCardClass}
                  >
                    <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">
                      {label}
                    </label>
                    {typeof displayEntity[key] === "boolean" ? (
                      <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={
                            basicDraft.dynamic[key] === true ||
                            basicDraft.dynamic[key] === "true"
                          }
                          onChange={(e) =>
                            setBasicDraft((d) => ({
                              ...d,
                              dynamic: {
                                ...d.dynamic,
                                [key]: e.target.checked ? "true" : "false",
                              },
                            }))
                          }
                          className="rounded border-gray-600 bg-gray-700 text-blue-600"
                        />
                        Enabled
                      </label>
                    ) : (
                      <input
                        type={
                          typeof displayEntity[key] === "number"
                            ? "number"
                            : "text"
                        }
                        value={basicDraft.dynamic[key] ?? ""}
                        onChange={(e) =>
                          setBasicDraft((d) => ({
                            ...d,
                            dynamic: {
                              ...d.dynamic,
                              [key]: e.target.value,
                            },
                          }))
                        }
                        className={inputCls}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={fieldGridClass}>
              {displayedBaseFields.map(({ key, label, value }) => {
                const isDescription = key === "description" || label === "Description";
                const isAlias = key === "alias_names" || label === "Alias Names";
                const empty = isEmptyValue(value);
                return (
                  <div
                    key={label}
                    className={`${fieldCardClass}${
                      isDescription || isAlias ? " md:col-span-2" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-xs uppercase tracking-wide text-gray-400">
                        {label}
                      </div>
                      {isDescription && displayEntity?.id ? (
                        <button
                          type="button"
                          disabled={
                            proposingDescription || savingBasic || !displayEntity?.id
                          }
                          onClick={handleProposeDescription}
                          className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {proposingDescription
                            ? "Working…"
                            : empty
                              ? "Generate from knowledge"
                              : "Refine from knowledge"}
                        </button>
                      ) : null}
                    </div>
                    {isDescription && descriptionProposal ? (
                      <div className="space-y-2">
                        <textarea
                          value={descriptionProposal.text}
                          onChange={(e) =>
                            setDescriptionProposal((prev) =>
                              prev
                                ? { ...prev, text: e.target.value }
                                : prev
                            )
                          }
                          rows={4}
                          disabled={savingBasic}
                          className="w-full text-sm text-gray-100 break-words whitespace-pre-wrap rounded border border-blue-700/50 bg-gray-900/60 px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                        {descriptionProposal.rationale ? (
                          <p className="text-xs text-gray-400">
                            {descriptionProposal.rationale}
                          </p>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            disabled={
                              savingBasic ||
                              !String(descriptionProposal.text || "").trim()
                            }
                            onClick={handleApplyDescriptionProposal}
                            className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                          >
                            {savingBasic ? "Saving…" : "Apply"}
                          </button>
                          <button
                            type="button"
                            disabled={savingBasic}
                            onClick={handleDiscardDescriptionProposal}
                            className="px-2 py-1 text-xs font-medium text-gray-300 border border-gray-600 rounded hover:bg-gray-700 disabled:opacity-50"
                          >
                            Discard
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`text-sm break-words whitespace-pre-wrap ${
                          empty ? "text-gray-500 italic" : "text-gray-100"
                        }`}
                      >
                        {empty
                          ? isDescription
                            ? "No description yet."
                            : isAlias
                              ? "No aliases yet."
                              : "—"
                          : String(value)}
                      </div>
                    )}
                    {isDescription && descriptionProposalError ? (
                      <p className="mt-1 text-xs text-amber-400">
                        {descriptionProposalError}
                      </p>
                    ) : null}
                  </div>
                );
              })}

              {dynamicFields.map(({ key, label, value }) => (
                <div
                  key={key}
                  className={fieldCardClass}
                >
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                    {label}
                  </div>
                  <div className="text-sm text-gray-100 break-words whitespace-pre-wrap">
                    {typeof value === "object"
                      ? JSON.stringify(value, null, 2)
                      : String(value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Dependencies */}
        {showHierarchySection && (
          <section>
            <div className={sectionTitleRowClass}>
              <h2 className="text-lg font-semibold text-white">
                Dependencies
              </h2>
              {allowEdit ? (
                <ReadSectionPencil
                  label="dependencies"
                  onClick={
                    typeof onRequestEdit === "function"
                      ? () => clickSectionEdit({ section: "hierarchy" })
                      : undefined
                  }
                />
              ) : null}
            </div>
            <div className={sectionStackClass}>
              {timelineTypeName ? (
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 bg-gray-800 rounded-lg border border-gray-700 ${
                    isMobile ? "gap-3 px-2.5 py-2.5" : "gap-4 px-3 py-3"
                  }`}
                >
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                      Successors
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        disabled={savingTimelineDeps || !displayEntity?.id}
                        onClick={() => setShowSuccessorModal(true)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {parseIdArray(displayEntity.successors).length > 0
                          ? `Successor (${parseIdArray(displayEntity.successors).length})`
                          : "Select successor"}
                      </button>
                      {parseIdArray(displayEntity.successors).length > 0 ? (
                        <button
                          type="button"
                          disabled={savingTimelineDeps}
                          onClick={() => persistTimelineField("successors", [])}
                          className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    {loadingTimelineSuccessors &&
                    parseIdArray(displayEntity.successors).length > 0 ? (
                      <p className="mt-2 text-xs text-gray-400">Loading…</p>
                    ) : null}
                    {!loadingTimelineSuccessors &&
                    timelineSuccessorEntities.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {timelineSuccessorEntities.map((ent) => (
                          <span
                            key={ent.id}
                            className="px-2 py-1 text-xs bg-purple-700 text-white rounded border border-purple-600"
                            title={ent.description || ent.name}
                          >
                            {ent.name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {!loadingTimelineSuccessors &&
                    parseIdArray(displayEntity.successors).length === 0 ? (
                      <p className="mt-2 text-sm text-gray-500 italic">
                        No successors
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
                      Predecessors
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        disabled={savingTimelineDeps || !displayEntity?.id}
                        onClick={() => setShowPredecessorModal(true)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {parseIdArray(displayEntity.predecessors).length > 0
                          ? `Predecessor (${parseIdArray(displayEntity.predecessors).length})`
                          : "Select predecessor"}
                      </button>
                      {parseIdArray(displayEntity.predecessors).length > 0 ? (
                        <button
                          type="button"
                          disabled={savingTimelineDeps}
                          onClick={() =>
                            persistTimelineField("predecessors", [])
                          }
                          className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    {loadingTimelinePredecessors &&
                    parseIdArray(displayEntity.predecessors).length > 0 ? (
                      <p className="mt-2 text-xs text-gray-400">Loading…</p>
                    ) : null}
                    {!loadingTimelinePredecessors &&
                    timelinePredecessorEntities.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {timelinePredecessorEntities.map((ent) => (
                          <span
                            key={ent.id}
                            className="px-2 py-1 text-xs bg-indigo-700 text-white rounded border border-indigo-600"
                            title={ent.description || ent.name}
                          >
                            {ent.name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {!loadingTimelinePredecessors &&
                    parseIdArray(displayEntity.predecessors).length === 0 ? (
                      <p className="mt-2 text-sm text-gray-500 italic">
                        No predecessors
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {displayEntity.parent || isTaxonomyEntity ? (
                <div className={fieldCardClass}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-xs uppercase tracking-wide text-gray-400">
                      {taxonomyCfg
                        ? `Parent ${taxonomyCfg.entityLabelLower}`
                        : "Parent"}
                    </div>
                    {isTaxonomyEntity && displayEntity?.id ? (
                      <div className="flex items-center gap-1.5">
                        {displayEntity.parent ? (
                          <button
                            type="button"
                            disabled={savingParent}
                            onClick={() => persistParent("")}
                            className="px-2 py-0.5 text-xs font-medium text-rose-300 hover:text-rose-200 border border-rose-700/50 rounded disabled:opacity-50"
                          >
                            Clear
                          </button>
                        ) : null}
                        <button
                          type="button"
                          disabled={savingParent}
                          onClick={() => setShowParentPicker(true)}
                          className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50"
                        >
                          {savingParent
                            ? "Saving…"
                            : displayEntity.parent
                              ? "Change in tree"
                              : "Pick from tree"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="text-sm text-gray-100 break-words">
                    {!displayEntity.parent
                      ? "No parent (top level)"
                      : loadingParentName && isEmptyValue(parentRef?.name)
                        ? "Loading…"
                        : parentRef?.name || "Unknown parent"}
                    {parentRef?.type && !isEmptyValue(parentRef?.name) ? (
                      <span className="ml-2 text-xs text-gray-400">
                        (
                        {getProfileByValue(parentRef.type)?.label ||
                          parentRef.type}
                        )
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {!timelineTypeName &&
                predecessorRefs.some((p) => !isEmptyValue(p.name)) && (
                  <div className={fieldCardClass}>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                      Predecessors
                    </div>
                    <ul className="list-disc list-inside text-sm text-gray-100 space-y-0.5">
                      {predecessorRefs
                        .filter((p) => !isEmptyValue(p.name))
                        .map((p) => (
                          <li key={p.id}>
                            {p.name}
                            {p.type && (
                              <span className="ml-2 text-xs text-gray-400">
                                ({p.type})
                              </span>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

              {!timelineTypeName &&
                successorRefs.some((s) => !isEmptyValue(s.name)) && (
                  <div className={fieldCardClass}>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                      Successors
                    </div>
                    <ul className="list-disc list-inside text-sm text-gray-100 space-y-0.5">
                      {successorRefs
                        .filter((s) => !isEmptyValue(s.name))
                        .map((s) => (
                          <li key={s.id}>
                            {s.name}
                            {s.type && (
                              <span className="ml-2 text-xs text-gray-400">
                                ({s.type})
                              </span>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
            </div>
          </section>
        )}

        {/* Lifecycle (custom-attribute section) — own inline edit toolbar */}
        {sectionAttrHints.lifecycle ? (
          <section>
            <div className={sectionTitleRowClass}>
              <h2 className="text-lg font-semibold text-white">Lifecycle</h2>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {editingCustomLifecycle && customAttributesDirty ? (
                  <>
                    <button
                      type="button"
                      disabled={savingCustomAttributes}
                      className="text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-3 py-1.5 rounded-md border border-gray-600"
                      onClick={cancelAnyCustomSectionEdit}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={savingCustomAttributes}
                      className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-md border border-blue-500/80"
                      onClick={handleSaveCustomAttributes}
                    >
                      {savingCustomAttributes ? "Saving…" : "Save"}
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-400 hover:bg-gray-700/80 transition-colors"
                  aria-label={
                    editingCustomLifecycle
                      ? "Stop editing Lifecycle fields"
                      : "Edit Lifecycle fields"
                  }
                  title={
                    editingCustomLifecycle
                      ? "Exit inline edit"
                      : "Edit Lifecycle attributes inline"
                  }
                  onClick={() =>
                    editingCustomLifecycle
                      ? cancelAnyCustomSectionEdit()
                      : beginLifecycleCustomEdit()
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                {typeof onRequestEdit === "function" ? (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded border border-transparent hover:border-gray-600"
                    onClick={() =>
                      clickSectionEdit({ section: "custom_attributes" })
                    }
                    title="Open full editor tab"
                  >
                    Full edit
                  </button>
                ) : null}
              </div>
            </div>
            {editingCustomLifecycle ? (
              <EntityCustomAttributes
                key={`inline-ca-${displayEntity.id}-${customInlineKeySuffix}`}
                entityForm={inlineEntityForm}
                setEntityForm={setInlineEntityForm}
                editingEntity={displayEntity}
                readOnly={!allowEdit}
                entityType={entityType}
                suppressIntro
                sectionsIncludeOnly={[CUSTOM_SECTION_LIFECYCLE]}
                omitSectionHeaders
              />
            ) : (
              <EntityCustomAttributesReadOnly
                embedWithoutHeading
                hideIntroParagraph
                omitSectionHeaders
                sectionsIncludeOnly={[CUSTOM_SECTION_LIFECYCLE]}
                entityType={entityType}
                entityTypeId={displayEntity.entity_type_id || null}
                customAttributes={customAttrs}
                headingAccessory={null}
              />
            )}
          </section>
        ) : null}

        {/* TIME Classification — custom-attribute section */}
        {sectionAttrHints.time ? (
          <section>
            <div className={sectionTitleRowClass}>
              <h2 className="text-lg font-semibold text-white">
                TIME Classification
              </h2>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {editingCustomTime && customAttributesDirty ? (
                  <>
                    <button
                      type="button"
                      disabled={savingCustomAttributes}
                      className="text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-3 py-1.5 rounded-md border border-gray-600"
                      onClick={cancelAnyCustomSectionEdit}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={savingCustomAttributes}
                      className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-md border border-blue-500/80"
                      onClick={handleSaveCustomAttributes}
                    >
                      {savingCustomAttributes ? "Saving…" : "Save"}
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-400 hover:bg-gray-700/80 transition-colors"
                  aria-label={
                    editingCustomTime
                      ? "Stop editing TIME Classification"
                      : "Edit TIME Classification"
                  }
                  title={
                    editingCustomTime
                      ? "Exit inline edit"
                      : "Edit TIME Classification inline"
                  }
                  onClick={() =>
                    editingCustomTime
                      ? cancelAnyCustomSectionEdit()
                      : beginTimeClassificationCustomEdit()
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                {typeof onRequestEdit === "function" ? (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded border border-transparent hover:border-gray-600"
                    onClick={() =>
                      clickSectionEdit({ section: "custom_attributes" })
                    }
                    title="Open full editor tab"
                  >
                    Full edit
                  </button>
                ) : null}
              </div>
            </div>
            {editingCustomTime ? (
              <EntityCustomAttributes
                key={`inline-ca-${displayEntity.id}-${customInlineKeySuffix}`}
                entityForm={inlineEntityForm}
                setEntityForm={setInlineEntityForm}
                editingEntity={displayEntity}
                readOnly={!allowEdit}
                entityType={entityType}
                suppressIntro
                sectionsIncludeOnly={[CUSTOM_SECTION_TIME]}
                omitSectionHeaders
              />
            ) : (
              <EntityCustomAttributesReadOnly
                embedWithoutHeading
                hideIntroParagraph
                omitSectionHeaders
                sectionsIncludeOnly={[CUSTOM_SECTION_TIME]}
                entityType={entityType}
                entityTypeId={displayEntity.entity_type_id || null}
                customAttributes={customAttrs}
                headingAccessory={null}
              />
            )}
          </section>
        ) : null}

        {/* Remaining custom attributes (excludes Lifecycle & TIME when typed) */}
        {sectionAttrHints.rest ? (
          <section>
            <div className={sectionTitleRowClass}>
              <h2 className="text-lg font-semibold text-white">
                Custom Attributes
              </h2>
              <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {editingCustomRest && customAttributesDirty ? (
                  <>
                    <button
                      type="button"
                      disabled={savingCustomAttributes}
                      className="text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-3 py-1.5 rounded-md border border-gray-600"
                      onClick={cancelAnyCustomSectionEdit}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={savingCustomAttributes}
                      className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-md border border-blue-500/80"
                      onClick={handleSaveCustomAttributes}
                    >
                      {savingCustomAttributes ? "Saving…" : "Save"}
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-blue-400 hover:bg-gray-700/80 transition-colors"
                  aria-label={
                    editingCustomRest
                      ? "Stop editing custom attributes"
                      : "Edit custom attributes"
                  }
                  title={
                    editingCustomRest
                      ? "Exit inline edit"
                      : "Edit custom attributes inline"
                  }
                  onClick={() =>
                    editingCustomRest
                      ? cancelAnyCustomSectionEdit()
                      : beginRestCustomAttributesEdit()
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                {typeof onRequestEdit === "function" ? (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1 rounded border border-transparent hover:border-gray-600"
                    onClick={() =>
                      clickSectionEdit({ section: "custom_attributes" })
                    }
                    title="Open full editor tab"
                  >
                    Full edit
                  </button>
                ) : null}
              </div>
            </div>
            {editingCustomRest ? (
              <EntityCustomAttributes
                key={`inline-ca-${displayEntity.id}-${customInlineKeySuffix}`}
                entityForm={inlineEntityForm}
                setEntityForm={setInlineEntityForm}
                editingEntity={displayEntity}
                readOnly={!allowEdit}
                entityType={entityType}
                suppressIntro
                sectionsExclude={CUSTOM_SECTION_EXCLUDED_FROM_REST}
              />
            ) : (
              <EntityCustomAttributesReadOnly
                embedWithoutHeading
                sectionsExclude={CUSTOM_SECTION_EXCLUDED_FROM_REST}
                entityType={entityType}
                entityTypeId={displayEntity.entity_type_id || null}
                customAttributes={customAttrs}
                headingAccessory={null}
              />
            )}
          </section>
        ) : null}

        {/* Relationships — enterprise connections (interfaces, capabilities, etc.). */}
        {enterpriseRelationshipSections.length > 0 && (
          <section>
            <div
              className={`${sectionTitleRowClass} !items-start`}
            >
              <h2 className="text-lg font-semibold text-white">
                Relationships
              </h2>
              {canRefineFromKnowledge ? (
                <button
                  type="button"
                  disabled={refiningBusy}
                  onClick={() => void handleRefineFromKnowledge(null)}
                  title="Get AI recommendations for all sections from linked knowledge"
                  className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                >
                  {refiningSectionKey === "__all__" ? (
                    "Working…"
                  ) : (
                    <>
                      <span className="md:hidden">AI all</span>
                      <span className="hidden md:inline">
                        AI recommendations (all)
                      </span>
                    </>
                  )}
                </button>
              ) : null}
            </div>
            {refineError ? (
              <div className="mb-3 rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
                {refineError}
              </div>
            ) : null}
            {refineStatusMessage ? (
              <div className="mb-3 rounded-lg border border-blue-800/60 bg-blue-950/30 px-3 py-2 text-sm text-blue-200">
                {refineStatusMessage}
              </div>
            ) : null}
            {relationshipsPopulateLoading ? (
              <div className="mb-3 rounded-lg border border-blue-800/60 bg-blue-950/30 px-3 py-2 text-sm text-blue-200 flex items-center gap-2">
                <RelationshipsLoadingSpinner className="w-4 h-4" />
                <span>Loading connections…</span>
              </div>
            ) : null}
            <div className="space-y-2">
              {enterpriseRelationshipSections.map(
                ({
                  key: sectionKey,
                  label: sectionLabel,
                  items,
                  sectionDef,
                  expectedRelCount = 0,
                }) => {
                  const sectionShowLoading =
                    items.length === 0 &&
                    (relationshipsLoading || neighborsHydrationLoading);
                  const pendingForSection =
                    pendingRelationshipDeletesBySection[sectionKey] || [];
                  const pendingDeleteCount = new Set(pendingForSection).size;
                  const pendingAddsList =
                    pendingAddsBySection[sectionKey] || [];
                  const pendingAddCount = pendingAddsList.length;
                  const pendingAttrUpdateCount = Object.keys(
                    pendingAttributeUpdatesBySection[sectionKey] || {}
                  ).length;
                  const dirtyTotal =
                    pendingDeleteCount +
                    pendingAddCount +
                    pendingAttrUpdateCount;
                  const sectionIsEditing =
                    editingRelationshipSectionKey === sectionKey;
                  const stagedAddIds = new Set(
                    pendingAddsList.map((p) => p.id).filter(Boolean)
                  );
                  const attrOverridesForSection =
                    pendingAttributeUpdatesBySection[sectionKey] || {};
                  const relatedTypeForLabel = normalizeEntityType(
                    sectionDef?.relatedEntityTypes?.[0] || "item"
                  );
                  const humanLabel = getHumanLabel(
                    sectionKey,
                    relatedTypeForLabel,
                    displayEntity.type ||
                      displayEntity.entity_type ||
                      "",
                    items.length
                  );
                  const relatedEntityTypeForSearch =
                    sectionDef?.relatedEntityTypes?.[0] || null;
                  const sectionIsEmpty = isEnterpriseRelSectionEmpty({
                    key: sectionKey,
                    items,
                  });
                  const hideEmptyOnMobile =
                    sectionIsEmpty && !showEmptyRelationshipSections;

                  return (
                    <div
                      key={sectionKey || sectionLabel}
                      className={`${fieldCardClass}${
                        hideEmptyOnMobile ? " hidden md:block" : ""
                      }${sectionIsEmpty ? " !py-1.5 md:!py-2" : ""}`}
                    >
                      {relationshipSectionErrors[sectionKey] ? (
                        <div className="bg-red-900/40 border border-red-700 rounded px-2 py-1.5 mb-2 text-xs text-red-200">
                          {relationshipSectionErrors[sectionKey]}
                        </div>
                      ) : null}
                      <div
                        className={`flex gap-2 ${
                          sectionIsEmpty
                            ? "items-center justify-between"
                            : "flex-col md:flex-row md:items-start md:justify-between"
                        } ${
                          items.length > 0 || sectionShowLoading
                            ? "mb-2"
                            : "mb-0 md:mb-2"
                        }`}
                      >
                        <div className="text-sm font-medium text-white flex-1 min-w-0 leading-snug">
                          <span className="break-words">{sectionLabel}</span>
                          {items.length > 0 ? (
                            <span className="ml-1.5 text-xs font-normal text-gray-400 tabular-nums">
                              ({items.length})
                            </span>
                          ) : sectionIsEmpty ? (
                            <span className="ml-1.5 text-xs font-normal text-gray-500 tabular-nums hidden md:inline">
                              (0)
                            </span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 flex-wrap justify-end">
                          {canRefineFromKnowledge ? (
                            <button
                              type="button"
                              disabled={refiningBusy || dirtyTotal > 0}
                              title={
                                dirtyTotal > 0
                                  ? "Save or cancel section edits first"
                                  : "Get AI recommendations for this section from linked knowledge"
                              }
                              onClick={() =>
                                void handleRefineFromKnowledge([sectionKey])
                              }
                              className="px-2 py-0.5 text-xs font-medium text-blue-300 hover:text-blue-200 border border-blue-700/60 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {refiningSectionKey === sectionKey ? (
                                "Working…"
                              ) : (
                                <>
                                  <span className="md:hidden">AI</span>
                                  <span className="hidden md:inline">
                                    AI recommendations
                                  </span>
                                </>
                              )}
                            </button>
                          ) : null}
                          {dirtyTotal > 0 ? (
                            <>
                              <button
                                type="button"
                                disabled={
                                  savingRelationshipSectionKey === sectionKey
                                }
                                className="text-xs font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-3 py-1.5 rounded-md border border-gray-600"
                                onClick={() =>
                                  handleCancelSectionRelationships(sectionKey)
                                }
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={
                                  savingRelationshipSectionKey === sectionKey
                                }
                                className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-md border border-blue-500/80"
                                onClick={() =>
                                  handleSaveSectionRelationships(sectionKey)
                                }
                              >
                                {savingRelationshipSectionKey === sectionKey
                                  ? "Saving…"
                                  : `Save${dirtyTotal > 1 ? ` (${dirtyTotal})` : ""}`}
                              </button>
                            </>
                          ) : null}
                          {sectionIsEditing ? (
                            <EntityRelationshipSection
                              variant="toolbar"
                              title=""
                              icon=""
                              items={items}
                              addLabel={humanLabel}
                              onAdd={(item) =>
                                handleStageRelationshipAdd(
                                  sectionKey,
                                  item,
                                  sectionDef,
                                  items
                                )
                              }
                              onRemove={() => {}}
                              showAdditionalFields={true}
                              readOnly={false}
                              maxItems={
                                getValidationRule(sectionKey)?.maxItems ?? null
                              }
                              typeMetaMap={relationshipTypeMetaMap}
                              relatedEntityType={relatedEntityTypeForSearch}
                              sourceEntityId={displayEntity?.id || null}
                              useLazyLoading={true}
                            />
                          ) : null}
                          {allowEdit ? (
                            <ReadSectionPencil
                              label={`Edit ${sectionLabel}`}
                              onClick={() =>
                                toggleRelationshipSectionEdit(sectionKey)
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                      {sectionShowLoading ? (
                        <p className="text-sm text-gray-400 italic flex items-center gap-2">
                          <RelationshipsLoadingSpinner />
                          Loading connections…
                        </p>
                      ) : items.length === 0 ? (
                        <p className="hidden md:block text-sm text-gray-500 italic">
                          No relationships in this section.
                        </p>
                      ) : (
                        <ul className="list-none text-sm text-gray-100 space-y-1.5 md:space-y-2">
                          {items.map((it, idx) => {
                            const neighborId =
                              it.id ||
                              it.target_entity_id ||
                              it.source_entity_id;
                            const other = resolveSectionListNeighbor(it);
                            if (!other || isEmptyValue(other.name))
                              return null;
                            const relId =
                              it.relationshipId ||
                              relationshipEdgeIdForSectionItem(
                                sectionDef,
                                displayEntity.id,
                                entityRelationships,
                                it
                              );
                            const stagedOverride = relId
                              ? attrOverridesForSection[relId]?.local
                              : null;
                            const activeFrom =
                              stagedOverride?.activeFrom ??
                              it.activeFrom ??
                              it.active_from ??
                              "";
                            const activeUntil =
                              stagedOverride?.activeUntil ??
                              it.activeUntil ??
                              it.active_until ??
                              "";
                            const description =
                              stagedOverride?.description ??
                              it.description ??
                              "";
                            const status =
                              stagedOverride?.status ?? it.status ?? "";
                            const statusDisplay = !isEmptyValue(status)
                              ? embedded
                                ? formatRelationshipStatusLabel(status) ||
                                  String(status)
                                : String(status)
                              : "";
                            const isStagedAdd = stagedAddIds.has(it.id);
                            const showRowActions =
                              sectionIsEditing &&
                              (Boolean(relId) || isStagedAdd);
                            const formatRelDate = (value) => {
                              if (!value) return "";
                              const d = new Date(value);
                              if (Number.isNaN(d.getTime()))
                                return String(value);
                              return d.toLocaleDateString();
                            };
                            return (
                              <li
                                key={
                                  relId ||
                                  it.id ||
                                  neighborId ||
                                  `${sectionKey}-${idx}`
                                }
                                className="flex gap-2 md:gap-3 justify-between items-start border-b border-gray-700/50 pb-1.5 md:pb-2 last:border-0 last:pb-0"
                              >
                                <div className="flex-1 min-w-0">
                                  <RelatedEntityNameLink
                                    related={other}
                                    currentEntityId={displayEntity?.id}
                                    onNavigate={handleNavigateToRelatedEntityGuarded}
                                    sectionIsEditing={sectionIsEditing}
                                    statusLabel={statusDisplay}
                                  />
                                  {(statusDisplay ||
                                    activeFrom ||
                                    activeUntil ||
                                    description ||
                                    showRowActions) && (
                                    <div className="mt-0.5 md:mt-1 space-y-0.5 text-xs text-gray-400">
                                      {statusDisplay ? (
                                        <div className="hidden md:block">
                                          Status:{" "}
                                          <span className="text-gray-300">
                                            {statusDisplay}
                                          </span>
                                        </div>
                                      ) : null}
                                      {activeFrom ? (
                                        <div>
                                          <span className="md:hidden">From </span>
                                          <span className="hidden md:inline">
                                            Active From:{" "}
                                          </span>
                                          <span className="text-gray-300">
                                            {formatRelDate(activeFrom)}
                                          </span>
                                        </div>
                                      ) : null}
                                      {activeUntil ? (
                                        <div>
                                          <span className="md:hidden">Until </span>
                                          <span className="hidden md:inline">
                                            Active Until:{" "}
                                          </span>
                                          <span className="text-gray-300">
                                            {formatRelDate(activeUntil)}
                                          </span>
                                        </div>
                                      ) : null}
                                      {!isEmptyValue(description) ? (
                                        <div className="text-gray-400 whitespace-pre-wrap break-words">
                                          {String(description)}
                                        </div>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                                {showRowActions ? (
                                  <div className="flex flex-col gap-1 shrink-0 items-end">
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-blue-300 hover:text-blue-200 px-2 py-1 rounded border border-blue-800/60 hover:bg-blue-950/40"
                                      onClick={() =>
                                        setEditingRelationshipAttributes({
                                          sectionKey,
                                          item: {
                                            ...it,
                                            name: other.name,
                                            activeFrom,
                                            activeUntil,
                                            status: status || "Proposed",
                                            description:
                                              description || "",
                                          },
                                          relationshipId: relId || null,
                                        })
                                      }
                                    >
                                      Edit details
                                    </button>
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-red-400 hover:text-red-300 px-2 py-1 rounded border border-red-800/60 hover:bg-red-950/40"
                                      onClick={() =>
                                        handleRelationshipRowRemove(
                                          sectionKey,
                                          relId,
                                          it.id
                                        )
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                }
              )}
              {emptyEnterpriseRelSectionCount > 0 ? (
                <button
                  type="button"
                  className="md:hidden w-full flex items-center justify-between gap-2 rounded-lg border border-dashed border-gray-600 bg-gray-800/40 px-3 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-800/70 hover:text-gray-100 transition-colors"
                  onClick={() =>
                    setShowEmptyRelationshipSections((open) => !open)
                  }
                  aria-expanded={showEmptyRelationshipSections}
                >
                  <span>
                    {showEmptyRelationshipSections
                      ? "Hide empty sections"
                      : `Show ${emptyEnterpriseRelSectionCount} empty section${
                          emptyEnterpriseRelSectionCount === 1 ? "" : "s"
                        }`}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                      showEmptyRelationshipSections ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              ) : null}
            </div>
          </section>
        )}

        {!isMobile &&
        displayEntity?.id &&
        isEnrichmentAnchorType(
          displayEntity.type ||
            displayEntity.entity_type ||
            displayEntity.entityType
        ) ? (
          <EntityReferenceSourcesSection
            entityId={displayEntity.id}
            entityName={displayEntity.name || displayEntity.entity_name || ""}
            entityType={
              displayEntity.type ||
              displayEntity.entity_type ||
              displayEntity.entityType
            }
            onNavigate={onNavigate}
          />
        ) : null}

        {!isMobile && displayEntity?.id ? (
          <EntityAuditTrailSection
            entityId={displayEntity.id}
            userDirectory={userDirectory}
            setUserDirectory={setUserDirectory}
            normalizeAuditJson={normalizeAuditJson}
            isMeaningfulAuditField={isMeaningfulAuditField}
            onRequestEdit={onRequestEdit}
            onEditHistory={() => clickSectionEdit({ section: "history" })}
            refreshToken={auditRefreshToken}
          />
        ) : null}
      </div>

      <EntitySelectorModal
        isOpen={showSuccessorModal}
        onClose={() => setShowSuccessorModal(false)}
        onSelect={async (selectedIds) => {
          setShowSuccessorModal(false);
          await persistTimelineField("successors", selectedIds);
        }}
        currentEntityType={timelineTypeName}
        currentEntityId={displayEntity?.id}
        title="Select Successor Entities"
        selectedEntityIds={parseIdArray(displayEntity?.successors)}
        allowMultiple={true}
      />

      <EntitySelectorModal
        isOpen={showPredecessorModal}
        onClose={() => setShowPredecessorModal(false)}
        onSelect={async (selectedIds) => {
          setShowPredecessorModal(false);
          await persistTimelineField("predecessors", selectedIds);
        }}
        currentEntityType={timelineTypeName}
        currentEntityId={displayEntity?.id}
        title="Select Predecessor Entities"
        selectedEntityIds={parseIdArray(displayEntity?.predecessors)}
        allowMultiple={true}
      />

      {isTaxonomyEntity ? (
        <HierarchyParentPickerModal
          isOpen={showParentPicker}
          onClose={() => setShowParentPicker(false)}
          onSelect={async (parentId, node) => {
            setShowParentPicker(false);
            await persistParent(parentId, node);
          }}
          entityType={entityTypeForTaxonomy}
          currentEntityId={displayEntity?.id}
          selectedParentId={displayEntity?.parent || ""}
          title={
            taxonomyCfg
              ? `Select parent ${taxonomyCfg.entityLabelLower}`
              : "Select parent"
          }
        />
      ) : null}

      <EnrichReviewDialog
        open={refineReviewOpen}
        theme="light"
        onClose={() => {
          setRefineReviewOpen(false);
          setRefinePreview(null);
          onRelationshipsPersisted?.();
        }}
        editingEntity={displayEntity}
        preview={refinePreview}
        reviewJobId={refineReviewJobId}
        onRelationshipsApplied={handleRefineRelationshipsApplied}
        onPreviewPatch={handleRefinePreviewPatch}
      />

      <RelationshipAttributesDialog
        key={
          editingRelationshipAttributes
            ? `${editingRelationshipAttributes.sectionKey}:${
                editingRelationshipAttributes.relationshipId ||
                editingRelationshipAttributes.item?.id ||
                "new"
              }`
            : "closed"
        }
        open={Boolean(editingRelationshipAttributes)}
        title="Edit relationship details"
        peerName={editingRelationshipAttributes?.item?.name || ""}
        initialFields={
          editingRelationshipAttributes
            ? relationshipAttributesFromItem(
                editingRelationshipAttributes.item
              )
            : null
        }
        saving={relationshipAttributesSaving}
        onClose={() => {
          if (relationshipAttributesSaving) return;
          setEditingRelationshipAttributes(null);
        }}
        onSave={({ api: apiPayload, local }) => {
          if (!editingRelationshipAttributes) return;
          setRelationshipAttributesSaving(true);
          try {
            handleStageRelationshipAttributeUpdate(
              editingRelationshipAttributes.sectionKey,
              editingRelationshipAttributes.item,
              editingRelationshipAttributes.relationshipId,
              { api: apiPayload, local }
            );
            setEditingRelationshipAttributes(null);
          } finally {
            setRelationshipAttributesSaving(false);
          }
        }}
      />
    </div>
  );
};

export default EntityReadOnlyView;
